import React, { useState, useEffect, useRef } from 'react';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, Legend, 
  ResponsiveContainer, CartesianGrid, ScatterChart, Scatter, AreaChart, Area, ComposedChart,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, FunnelChart, Funnel
} from 'recharts';
import { FaPlus, FaSave, FaTrash, FaTextHeight, FaTable, FaChartBar, FaChartLine, FaChartPie, FaTimes, FaCog, FaCalendarAlt, FaUsers, FaFilter, FaLayerGroup, FaDatabase, FaPalette, FaAlignLeft, FaAlignCenter, FaAlignRight, FaFont, FaEdit } from 'react-icons/fa';
import { ChromePicker } from 'react-color';
import { Rnd } from 'react-rnd';
import { useParams, useNavigate } from 'react-router-dom';

// Import the table configuration
import tableConfig from '../config/tableConfig.json';
const API_URL = '/api';

// Convert the tableConfig to screensData format
const screensData = Object.keys(tableConfig).map((screenName, index) => {
  const screenData = tableConfig[screenName];
  return {
    id: index + 1,
    name: screenName,
    headerTable: screenData.Header ? screenName : null,
    detailTable: screenData.Details ? screenName + '_Details' : null,
    apiEndpoints: screenData.API || [],
    showOnHeader: screenData.Header ? 1 : 0,
    showOnItem: screenData.Details ? 1 : 0,
    showOnMaster: 1,
    showOnDetails: 0
  };
});

// Convert the tableConfig to tableColumns format
const tableColumns = {};
Object.keys(tableConfig).forEach(screenName => {
  const screenData = tableConfig[screenName];
  if (screenData.Header) {
    tableColumns[screenName] = screenData.Header;
  }
  if (screenData.Details) {
    tableColumns[screenName + '_Details'] = screenData.Details;
  }
});

// Mock users for assignment
const mockUsers = [
  { id: 1, name: 'John Doe', email: 'john@example.com', role: 'Admin' },
  { id: 2, name: 'Jane Smith', email: 'jane@example.com', role: 'Manager' },
  { id: 3, name: 'Robert Johnson', email: 'robert@example.com', role: 'Analyst' },
  { id: 4, name: 'Emily Davis', email: 'emily@example.com', role: 'Viewer' }
];

// Chart colors
const COLORS = ['#033A3E', '#075D5F', '#f1fbfb', '#0f1724', '#6b7280'];

// Function to determine column type
// Function to determine column type
const getColumnType = (columnName) => {
  const name = columnName.toLowerCase();
  
  // Date/Time columns
  if (name.includes('date') || name.includes('time') || name.includes('createdat') || name.includes('updatedat')) {
    return 'date';
  }
  
  // Numeric columns
  if (name.includes('amount') || name.includes('price') || name.includes('quantity') || 
      name.includes('total') || name.includes('rate') || name.includes('value') || 
      name.includes('discount') || name.includes('tax') || name.includes('number') ||
      name.includes('id') && !name.includes('code')) {
    return 'numeric';
  }
  
  // ID/Text fields
  if (name.includes('code') || name.includes('id') || name.includes('number') && !name.includes('order')) {
    return 'id';
  }
  
  // Default to categorical
  return 'categorical';
};

// Function to suggest visualization based on selected columns
const suggestVisualization = (columns) => {
  if (columns.length === 0) return '';
  
  const columnTypes = columns.map(col => getColumnType(col));
  const hasNumeric = columnTypes.includes('numeric');
  const hasDate = columnTypes.includes('date');
  const hasCategorical = columnTypes.includes('categorical');
  const hasId = columnTypes.includes('id');
  
  // Rules for combination of columns
  if (columns.length === 1) {
    const type = columnTypes[0];
    if (type === 'numeric') return 'bar';
    if (type === 'date') return 'line';
    if (type === 'categorical') return 'bar';
    return 'table';
  }
  
  if (columns.length === 2) {
    if (hasNumeric && columnTypes.filter(t => t === 'numeric').length === 2) return 'scatter';
    if (hasNumeric && hasCategorical) return 'bar';
    if (hasNumeric && hasDate) return 'line';
    if (hasCategorical && hasCategorical) return 'stackedbar';
    return 'table';
  }
  
  if (columns.length === 3) {
    if (hasNumeric && hasNumeric && hasCategorical) return 'groupedbar';
    if (hasNumeric && hasDate && hasCategorical) return 'multiline';
    if (hasNumeric && hasNumeric && hasDate) return 'multiline';
    return 'table';
  }
  
  // For 4+ columns, default to table
  return 'table';
};

// Function to aggregate data for charts - UPDATED TO SUPPORT TWO VALUE FIELDS
// Function to aggregate data for charts - UPDATED FOR PROPER GROUPING AND SUMMATION
const aggregateDataForChart = (data, chartType, groupBy, valueField, valueField2 = null) => {
  if (!data || data.length === 0) return [];
  
  console.log(`Aggregating data for ${chartType} chart. Group by: ${groupBy}, Value field: ${valueField}, Value field2: ${valueField2}`);
  console.log('Sample data:', data.slice(0, 2));
  
  // For scatter plot, we don't need aggregation
  if (chartType === 'scatter') {
    return data.map(item => ({
      x: parseFloat(item[groupBy]) || 0,
      y: parseFloat(item[valueField]) || 0,
      name: item.name || item[Object.keys(item).find(k => k !== groupBy && k !== valueField)] || ''
    }));
  }
  
  // Check if the groupBy and valueField exist in the data
  if (data.length > 0 && (!data[0].hasOwnProperty(groupBy) || !data[0].hasOwnProperty(valueField))) {
    console.error(`groupBy field '${groupBy}' or valueField '${valueField}' not found in data`);
    return [];
  }
  
  // Aggregate data for other chart types
  const aggregated = {};
  data.forEach(item => {
    const key = item[groupBy];
    
    // Handle numeric values that might be strings
    let value = 0;
    if (typeof item[valueField] === 'string') {
      // Remove currency symbols and commas, then convert to number
      value = parseFloat(item[valueField].replace(/[^0-9.-]+/g, '')) || 0;
    } else {
      value = parseFloat(item[valueField]) || 0;
    }
    
    let value2 = 0;
    if (valueField2) {
      if (typeof item[valueField2] === 'string') {
        value2 = parseFloat(item[valueField2].replace(/[^0-9.-]+/g, '')) || 0;
      } else {
        value2 = parseFloat(item[valueField2]) || 0;
      }
    }
    
    if (aggregated[key]) {
      aggregated[key].value += value;
      if (valueField2 !== null) {
        aggregated[key].value2 += value2;
      }
    } else {
      aggregated[key] = { value };
      if (valueField2 !== null) {
        aggregated[key].value2 = value2;
      }
    }
  });
  
  // Convert to array format for charts
  const result = Object.keys(aggregated).map(key => ({
    name: key,
    value: aggregated[key].value,
    ...(valueField2 !== null && { value2: aggregated[key].value2 })
  }));
  
  console.log('Aggregated result:', result);
  return result;
};

// Function to fetch data from API
const fetchDataFromAPI = async (sourceType, sourceName, selectedTables) => {
  try {
    console.log(`Fetching data from ${API_URL} for ${sourceType}: ${sourceName}`);
    
    // Get authentication token from localStorage or state
    const token = localStorage.getItem('authToken') || '';
    
    // Prepare headers
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
    
    let endpoint;
    if (sourceType === 'screen') {
      // Find the screen data to get the API endpoint
      const screenData = screensData.find(s => s.name === sourceName);
      if (!screenData || screenData.apiEndpoints.length === 0) {
        throw new Error(`No API endpoint found for screen: ${sourceName}`);
      }
      endpoint = screenData.apiEndpoints[0];
      console.log(`Using screen endpoint: ${endpoint}`);
    } else if (sourceType === 'view') {
      // For views, use the new endpoint that fetches data from any view
      // Extract the view name without schema (e.g., "dbo.ViewName" -> "ViewName")
      const viewName = sourceName.split('.').pop();
      endpoint = `/db-view-data/dbo.${encodeURIComponent(viewName)}`;
      console.log(`Using view endpoint: ${endpoint}`);
      console.log("using viewName " + viewName);
    } else {
      throw new Error('Invalid source type');
    }
    
    // Make API request
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'GET',
      headers: headers
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    console.log("API response data:", data);
    
    // Handle different response formats
    let headerData = [];
    let detailsData = [];
    
    // If data is an array, we need to determine if it's header or details
    if (Array.isArray(data)) {
      // For screens with both header and details, we need to check the structure
      if (sourceType === 'screen') {
        const screenConfig = screensData.find(s => s.name === sourceName);
        
        if (screenConfig && screenConfig.detailTable) {
          // This screen has both header and details
          // Check if the data has nested Items or TransactionEntries arrays
          if (data.length > 0) {
            const firstRecord = data[0];
            
            // Check if this record has nested arrays (Items, TransactionEntries, etc.)
            const nestedArrays = Object.keys(firstRecord).filter(key => 
              Array.isArray(firstRecord[key])
            );
            
            if (nestedArrays.length > 0) {
              // This looks like header data with nested details
              headerData = data;
              
              // Extract details from nested arrays
              nestedArrays.forEach(arrayKey => {
                data.forEach(header => {
                  if (header[arrayKey] && Array.isArray(header[arrayKey])) {
                    // Add a reference to the parent header record
                    const details = header[arrayKey].map(detail => ({
                      ...detail,
                      _parentRef: header[Object.keys(header).find(k => 
                        k.toLowerCase().includes('id') || 
                        k.toLowerCase().includes('code') || 
                        k.toLowerCase().includes('number')
                      )] || 'unknown'
                    }));
                    detailsData = [...detailsData, ...details];
                  }
                });
              });
            } else {
              // All data is treated as header
              headerData = data;
            }
          }
        } else {
          // This screen only has header data
          headerData = data;
        }
      } else {
        // For views, all data is treated as a single table
        headerData = data;
      }
    } 
    // Handle the new view data format
    else if (data.data && Array.isArray(data.data)) {
      // This is the new view endpoint format
      headerData = data.data;
      detailsData = [];
    }
    // If data has header and details properties
    else if (data.header && data.details) {
      headerData = Array.isArray(data.header) ? data.header : [];
      detailsData = Array.isArray(data.details) ? data.details : [];
    }
    // If data has recordset property (common in SQL Server responses)
    else if (data.recordset) {
      headerData = Array.isArray(data.recordset) ? data.recordset : [];
    }
    // If data is an object but not array, try to find array properties
    else if (typeof data === 'object' && data !== null) {
      // Look for array properties
      const arrayProps = Object.keys(data).filter(key => Array.isArray(data[key]));
      if (arrayProps.length > 0) {
        headerData = data[arrayProps[0]];
        if (arrayProps.length > 1) {
          detailsData = data[arrayProps[1]];
        }
      }
    }
    
    console.log("Processed header data:", headerData.slice(0, 2));
    console.log("Processed details data:", detailsData.slice(0, 2));
    
    return {
      header: headerData,
      details: detailsData
    };
  } catch (error) {
    console.error('Error fetching data:', error);
    // Return empty data on error
    return { header: [], details: [] };
  }
};

const DraggableLabel = ({ 
  value, 
  x, 
  y, 
  rotation = 0, 
  color = '#000', 
  fontSize = 12, 
  fontFamily = 'Arial',
  onPositionChange, 
  onRotationChange,
  onColorChange,
  onFontSizeChange,
  onFontFamilyChange,
  onTextChange,
  isEditing = false,
  onEditStart,
  onEditEnd
}) => {
  const [pos, setPos] = useState({ x, y });
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isRotating, setIsRotating] = useState(false);
  const [startAngle, setStartAngle] = useState(0);
  const [currentRotation, setCurrentRotation] = useState(rotation);
  const [currentValue, setCurrentValue] = useState(value);
  const [isEditingText, setIsEditingText] = useState(isEditing);
  const labelRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setPos({ x, y });
    setCurrentRotation(rotation);
    setCurrentValue(value);
  }, [x, y, rotation, value]);

  useEffect(() => {
    if (isEditingText && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditingText]);

  const handlePointerDown = (e) => {
    if (e.shiftKey) {
      // Shift+click for rotation
      setIsRotating(true);
      const rect = labelRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const angle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * 180 / Math.PI;
      setStartAngle(angle - currentRotation);
      e.target.setPointerCapture(e.pointerId);
    } else if (e.detail === 2) { // Double click to edit text
      setIsEditingText(true);
      onEditStart && onEditStart();
    } else {
      // Regular click for dragging
      e.preventDefault();
      e.stopPropagation();
      setDragging(true);
      setOffset({
        x: e.clientX - pos.x,
        y: e.clientY - pos.y,
      });
      e.target.setPointerCapture(e.pointerId);
      e.target.style.cursor = "grabbing";
    }
  };

  const handlePointerMove = (e) => {
    if (isRotating) {
      const rect = labelRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const angle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * 180 / Math.PI;
      const newRotation = angle - startAngle;
      setCurrentRotation(newRotation);
      onRotationChange(newRotation);
    } else if (dragging) {
      const newPos = {
        x: e.clientX - offset.x,
        y: e.clientY - offset.y,
      };
      setPos(newPos);
      onPositionChange(newPos);
    }
  };

  const handlePointerUp = (e) => {
    if (isRotating) {
      setIsRotating(false);
      e.target.releasePointerCapture(e.pointerId);
    } else if (dragging) {
      setDragging(false);
      e.target.releasePointerCapture(e.pointerId);
      e.target.style.cursor = "grab";
    }
  };

  const handleTextChange = (e) => {
    setCurrentValue(e.target.value);
  };

  const handleTextBlur = () => {
    setIsEditingText(false);
    onTextChange(currentValue);
    onEditEnd && onEditEnd();
  };

  const handleTextKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleTextBlur();
    }
  };

  if (isEditingText) {
    return (
      <foreignObject x={pos.x - 50} y={pos.y - 15} width="100" height="30">
        <input
          ref={inputRef}
          type="text"
          value={currentValue}
          onChange={handleTextChange}
          onBlur={handleTextBlur}
          onKeyDown={handleTextKeyDown}
          style={{
            fontSize: `${fontSize}px`,
            fontFamily: fontFamily,
            color: color,
            background: 'white',
            border: '1px solid #ccc',
            borderRadius: '3px',
            padding: '2px 5px',
            textAlign: 'center',
            width: '100%'
          }}
        />
      </foreignObject>
    );
  }

  return (
    <text
      ref={labelRef}
      x={pos.x}
      y={pos.y}
      transform={`rotate(${currentRotation}, ${pos.x}, ${pos.y})`}
      style={{ 
        cursor: "grab", 
        userSelect: "none",
        fill: color,
        fontSize: `${fontSize}px`,
        fontFamily: fontFamily
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      {currentValue}
    </text>
  );
};

const DashboardBuilder = () => {
  const [dashboards, setDashboards] = useState([]);
  const [currentDashboard, setCurrentDashboard] = useState(null);
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showBackgroundColorPicker, setShowBackgroundColorPicker] = useState(false);
  const [showTableColorPicker, setShowTableColorPicker] = useState(false);
  const [showTitleColorPicker, setShowTitleColorPicker] = useState(false);
  const [showXColorPicker, setShowXColorPicker] = useState(false);
  const [showYColorPicker, setShowYColorPicker] = useState(false);
  const [animationType, setAnimationType] = useState('none');
  const [dashboardName, setDashboardName] = useState('My Dashboard');
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const [leftPanelOpen, setLeftPanelOpen] = useState(false);
  
  // Configuration states
  const [sourceType, setSourceType] = useState('screen'); // 'screen' or 'view'
  const [selectedScreen, setSelectedScreen] = useState('');
  const [selectedView, setSelectedView] = useState('');
  const [viewsData, setViewsData] = useState({}); // To store views and their columns
  const [selectedTables, setSelectedTables] = useState({ header: false, details: false });
  const [availableColumns, setAvailableColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [visualizationType, setVisualizationType] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  // UPDATED: Added valueField2 to chart configuration
  const [chartConfig, setChartConfig] = useState({ groupBy: '', valueField: '', valueField2: '' });
  const [apiData, setApiData] = useState({ header: [], details: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [apiError, setApiError] = useState('');

const [dashboardCode, setDashboardCode] = useState(null);
const [isSaving, setIsSaving] = useState(false);
const [showAssignModal, setShowAssignModal] = useState(false);
const [users, setUsers] = useState([]);
const navigate = useNavigate();
const { id } = useParams();


// Add this useEffect to fetch dashboard data if editing
useEffect(() => {
  if (id) {
    setDashboardCode(id);
    fetchDashboard(id);
  }
  fetchUsers();
}, [id]);

  // Axis label states
  const [xLabelProps, setXLabelProps] = useState({ 
    x: 200, 
    y: 310, 
    rotation: 0, 
    color: '#000', 
    fontSize: 12, 
    fontFamily: 'Arial',
    text: ''
  });
  const [yLabelProps, setYLabelProps] = useState({ 
    x: 20, 
    y: 150, 
    rotation: -90, 
    color: '#000', 
    fontSize: 12, 
    fontFamily: 'Arial',
    text: ''
  });
  const [editingAxis, setEditingAxis] = useState(null); // 'x' or 'y'
  
  // Color customization states
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [tableHeaderColor, setTableHeaderColor] = useState('#f3f4f6');
  const [tableRowColor, setTableRowColor] = useState('#ffffff');
  const [tableAltRowColor, setTableAltRowColor] = useState('#f9fafb');
  
  // Dynamic height state
  const [containerHeight, setContainerHeight] = useState('100vh');
  const containerRef = useRef(null);
  
  // Find selected element
  const selectedElement = elements.find(el => el.id === selectedId);

  // Initialize a new dashboard
  const initNewDashboard = () => {
    setCurrentDashboard({
      id: Date.now(),
      name: 'New Dashboard',
      createdAt: new Date().toISOString(),
      elements: []
    });
    setElements([]);
    setSelectedId(null);
    setRightPanelOpen(false);
    setLeftPanelOpen(true);
    setDashboardName('New Dashboard');
    // Reset axis labels
    setXLabelProps({ 
      x: 200, 
      y: 310, 
      rotation: 0, 
      color: '#000', 
      fontSize: 12, 
      fontFamily: 'Arial',
      text: ''
    });
    setYLabelProps({ 
      x: 20, 
      y: 150, 
      rotation: -90, 
      color: '#000', 
      fontSize: 12, 
      fontFamily: 'Arial',
      text: ''
    });
    setEditingAxis(null);
    setShowXColorPicker(false);
    setShowYColorPicker(false);
  };

  // Function to combine and aggregate header and details data
 // Function to combine and aggregate header and details data
// Function to combine and aggregate header and details data - UPDATED FOR PROPER GROUPING
const combineHeaderAndDetailsData = (headerData, detailsData, selectedColumns, selectedTables) => {
  // Ensure headerData and detailsData are arrays
  headerData = Array.isArray(headerData) ? headerData : [];
  detailsData = Array.isArray(detailsData) ? detailsData : [];
  
  // For views, we only have header data
  if (!selectedTables.header && !selectedTables.details) {
    return headerData;
  }

  if (!selectedTables.header || !selectedTables.details) {
    // If only one table type is selected, return the corresponding data
    return selectedTables.header ? headerData : detailsData;
  }

  // If both header and details are selected, we need to combine them
  let headerPrimaryKey = 'id';
  if (headerData.length > 0) {
    headerPrimaryKey = Object.keys(headerData[0]).find(key => 
      key.toLowerCase().includes('id') || 
      key.toLowerCase().includes('code') || 
      key.toLowerCase().includes('number')
    ) || 'id';
  }

  const detailsForeignKey = '_parentRef';

  // Create a map of header records for quick lookup
  const headerMap = {};
  headerData.forEach(header => {
    const key = header[headerPrimaryKey];
    if (key) {
      headerMap[key] = header;
    }
  });

  // Combine each detail record with its corresponding header record
  const combinedData = [];
  detailsData.forEach(detail => {
    const headerKey = detail[detailsForeignKey];
    if (headerKey && headerMap[headerKey]) {
      const header = headerMap[headerKey];
      const combinedRecord = {};
      
      // Add all selected columns from both header and details
      selectedColumns.forEach(col => {
        if (header.hasOwnProperty(col)) {
          combinedRecord[col] = header[col];
        } else if (detail.hasOwnProperty(col)) {
          combinedRecord[col] = detail[col];
        } else {
          combinedRecord[col] = null;
        }
      });
      
      combinedData.push(combinedRecord);
    }
  });

  return combinedData;
};

  // Add new element to dashboard
  const addElement = () => {
  const sourceName = sourceType === 'screen' ? selectedScreen : selectedView;
  
  if (!sourceName || !visualizationType || selectedColumns.length === 0) {
    alert('Please complete all required fields');
    return;
  }

  // Check if data is available
  if (!apiData || (!apiData.header && !apiData.details)) {
    alert('Data not available. Please wait for data to load or check your connection.');
    return;
  }

  let apiEndpoints = [];
  if (sourceType === 'screen') {
    const screenData = screensData.find(s => s.name === sourceName);
    if (screenData) {
      apiEndpoints = screenData.apiEndpoints;
    }
  }

  const id = Date.now();
  
  // Get the appropriate data based on source type
  let data;
  if (sourceType === 'view') {
    // For views, just use the header data directly
    data = apiData.header || [];
  } else {
    // For screens, combine header and details data if needed
    data = combineHeaderAndDetailsData(
      apiData.header || [], 
      apiData.details || [], 
      selectedColumns, 
      selectedTables
    );
  }
  
  console.log('Data after combining:', data.slice(0, 2));
  
  // Apply filters
  let filteredData = [...data];
  
  // Apply date range filter if specified
  if (dateRange.from && dateRange.to) {
    filteredData = filteredData.filter(item => {
      // Find date column
      const dateColumn = Object.keys(item).find(key => 
        key.toLowerCase().includes('date') || 
        key.toLowerCase().includes('createdat') || 
        key.toLowerCase().includes('updatedat')
      );
      
      if (!dateColumn) return true;
      
      const itemDate = new Date(item[dateColumn]);
      const fromDate = new Date(dateRange.from);
      const toDate = new Date(dateRange.to);
      return itemDate >= fromDate && itemDate <= toDate;
    });
  }
  
  // Apply column filters
  Object.keys(filters).forEach(column => {
    if (filters[column]) {
      filteredData = filteredData.filter(item => {
        const value = String(item[column]).toLowerCase();
        const filterValue = String(filters[column]).toLowerCase();
        return value.includes(filterValue);
      });
    }
  });
  
  // Filter by selected columns
  filteredData = filteredData.map(item => {
    const filteredItem = {};
    selectedColumns.forEach(col => {
      if (item[col] !== undefined) {
        filteredItem[col] = item[col];
      }
    });
    return filteredItem;
  });

  // Ensure numeric values are properly converted
  filteredData = filteredData.map(item => {
    const newItem = { ...item };
    selectedColumns.forEach(col => {
      if (getColumnType(col) === 'numeric' && typeof newItem[col] === 'string') {
        // Remove currency symbols and commas, then convert to number
        const numericValue = parseFloat(newItem[col].replace(/[^0-9.-]+/g, ''));
        if (!isNaN(numericValue)) {
          newItem[col] = numericValue;
        }
      }
    });
    return newItem;
  });

  console.log('Final filtered data for element:', filteredData.slice(0, 3));

  // Get axis labels
  const getXAxisLabel = () => {
    if (visualizationType === 'scatter') {
      return chartConfig.groupBy || 'X-Axis';
    }
    return chartConfig.groupBy || 'Category';
  };
  
  const getYAxisLabel = () => {
    if (visualizationType === 'scatter') {
      return chartConfig.valueField || 'Y-Axis';
    }
    return chartConfig.valueField || 'Value';
  };

  // Create new element with secondary value color for double-valued charts
  const newElement = {
    id,
    type: visualizationType,
    sourceType, // 'screen' or 'view'
    sourceName: sourceType === 'view' ? selectedView : selectedScreen,
    tables: selectedTables,
    columns: selectedColumns,
    dateRange,
    apiEndpoints, 
    filters,
    data: filteredData,
    x: 300,
    y: 200,
    width: visualizationType === 'text' ? 200 : 400,
    height: visualizationType === 'text' ? 50 : 300,
    text: visualizationType === 'text' ? 'New Text' : '',
    fontSize: 16,
    fontFamily: 'Arial',
    fill: '#000000',
    chartType: visualizationType.includes('chart') ? visualizationType : null,
    animation: animationType,
    chartConfig,
    assignedUsers: selectedUsers,
    // Color customization
    backgroundColor,
    tableHeaderColor,
    tableRowColor,
    tableAltRowColor,
    // Title and alignment
    title: '',
    titleAlignment: 'center',
    titlePadding: 0,
    titleFontSize: 14,
    titleColor: '#000000',
    // Axis labels
    axisLabels: {
      x: { 
        ...xLabelProps, 
        text: getXAxisLabel() 
      },
      y: { 
        ...yLabelProps, 
        text: getYAxisLabel() 
      }
    },
    // Add secondary value color for double-valued charts
    ...(visualizationType === 'doubleline' || 
      visualizationType === 'stackedarea' || 
      visualizationType === 'stackbar' || 
      visualizationType === 'composed') && {
      secondaryValueColor: COLORS[1] // Default secondary color
    }
  };
  
  setElements([...elements, newElement]);
  setSelectedId(id);
  setRightPanelOpen(true);
  setLeftPanelOpen(false);
  
  // Reset configuration for next element
  setSourceType('screen');
  setSelectedScreen('');
  setSelectedView('');
  setSelectedTables({ header: false, details: false });
  setSelectedColumns([]);
  setDateRange({ from: '', to: '' });
  setVisualizationType('');
  setSelectedUsers([]);
  setChartConfig({ groupBy: '', valueField: '', valueField2: '' });
  setFilters({});
  setApiError('');
  // Reset colors
  setBackgroundColor('#ffffff');
  setTableHeaderColor('#f3f4f6');
  setTableRowColor('#ffffff');
  setTableAltRowColor('#f9fafb');
  // Reset axis labels
  setXLabelProps({ 
    x: 200, 
    y: 310, 
    rotation: 0, 
    color: '#000', 
    fontSize: 12, 
    fontFamily: 'Arial',
    text: ''
  });
  setYLabelProps({ 
    x: 20, 
    y: 150, 
    rotation: -90, 
    color: '#000', 
    fontSize: 12, 
    fontFamily: 'Arial',
    text: ''
  });
  setEditingAxis(null);
  setShowXColorPicker(false);
  setShowYColorPicker(false);
};

  // Update element properties
  const updateElement = (id, props) => {
    setElements(elements.map(el => 
      el.id === id ? { ...el, ...props } : el
    ));
  };

  // Update axis label properties
  const updateAxisLabel = (axis, props) => {
    if (!selectedElement) return;
    
    const updatedAxisLabels = {
      ...selectedElement.axisLabels,
      [axis]: {
        ...selectedElement.axisLabels[axis],
        ...props
      }
    };
    
    updateElement(selectedId, { axisLabels: updatedAxisLabels });
    
    // Also update local state for immediate feedback
    if (axis === 'x') {
      setXLabelProps(updatedAxisLabels.x);
    } else {
      setYLabelProps(updatedAxisLabels.y);
    }
  };

  // Delete selected element
  const deleteElement = () => {
    if (selectedId) {
      setElements(elements.filter(el => el.id !== selectedId));
      setSelectedId(null);
      setRightPanelOpen(false);
      setEditingAxis(null);
      setShowXColorPicker(false);
      setShowYColorPicker(false);
    }
  };

 // Add these functions to the DashboardBuilder component
const fetchDashboard = async (code) => {
  try {
    const token = localStorage.getItem('authToken') || '';
    const response = await fetch(`${API_URL}/dashboards/${code}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) throw new Error('Failed to fetch dashboard');
    
    const dashboard = await response.json();
    setCurrentDashboard(dashboard);
    setDashboardName(dashboard.DashboardName);
    
    // Parse the design JSON
    if (dashboard.DesignJSON) {
      try {
        const design = JSON.parse(dashboard.DesignJSON);
        let elements = design.elements || [];
        
        // Fetch data for each element
        const elementsWithData = await Promise.all(elements.map(async (element) => {
          // Fetch fresh data for this element
          const apiData = await fetchDataFromAPI(element.sourceType, element.sourceName, element.tables);
          
          // Combine data if needed
          const combinedData = combineHeaderAndDetailsData(
            apiData.header, 
            apiData.details, 
            element.columns, 
            element.tables
          );
          
          // Apply filters if any
          let filteredData = [...combinedData];
          
          // Apply date range filter if specified
          if (element.dateRange && element.dateRange.from && element.dateRange.to) {
            filteredData = filteredData.filter(item => {
              const dateColumn = Object.keys(item).find(key => 
                key.toLowerCase().includes('date') || 
                key.toLowerCase().includes('createdat') || 
                key.toLowerCase().includes('updatedat')
              );
              
              if (!dateColumn) return true;
              
              const itemDate = new Date(item[dateColumn]);
              const fromDate = new Date(element.dateRange.from);
              const toDate = new Date(element.dateRange.to);
              return itemDate >= fromDate && itemDate <= toDate;
            });
          }
          
          // Apply column filters
          if (element.filters) {
            Object.keys(element.filters).forEach(column => {
              if (element.filters[column]) {
                filteredData = filteredData.filter(item => {
                  const value = String(item[column]).toLowerCase();
                  const filterValue = String(element.filters[column]).toLowerCase();
                  return value.includes(filterValue);
                });
              }
            });
          }
          
          // Filter by selected columns
          filteredData = filteredData.map(item => {
            const filteredItem = {};
            element.columns.forEach(col => {
              if (item[col] !== undefined) {
                filteredItem[col] = item[col];
              }
            });
            return filteredItem;
          });

          // Ensure numeric values are properly converted
          filteredData = filteredData.map(item => {
            const newItem = { ...item };
            element.columns.forEach(col => {
              if (getColumnType(col) === 'numeric' && typeof newItem[col] === 'string') {
                const numericValue = parseFloat(newItem[col].replace(/[^0-9.-]+/g, ''));
                if (!isNaN(numericValue)) {
                  newItem[col] = numericValue;
                }
              }
            });
            return newItem;
          });

          return {
            ...element,
            data: filteredData
          };
        }));
        
        setElements(elementsWithData);
      } catch (e) {
        console.error('Error parsing dashboard design:', e);
      }
    }
  } catch (error) {
    console.error('Error fetching dashboard:', error);
  }
};

const fetchUsers = async () => {
  try {
    const token = localStorage.getItem('authToken') || '';
    const response = await fetch(`${API_URL}/users`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) throw new Error('Failed to fetch users');
    
    const data = await response.json();
    setUsers(data);
  } catch (error) {
    console.error('Error fetching users:', error);
  }
};

// Update the saveDashboard function
const saveDashboard = async () => {
  setIsSaving(true);
  
  try {
    const token = localStorage.getItem('authToken') || '';

       // Create a clean version of elements without data
    const cleanElements = elements.map(el => {
      const { data, _logged, ...rest } = el; // Remove data and debug properties
      return rest;
    });
    
    const dashboardData = {
      DashboardName: dashboardName,
      DesignJSON: JSON.stringify({ elements: cleanElements }),
      IsActive: 1
    };
    
    let response;
    if (dashboardCode) {
      // Update existing dashboard using code
      response = await fetch(`${API_URL}/dashboards/${dashboardCode}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(dashboardData)
      });
       console.log('Updating dashboard with code:', dashboardCode);
    } else {
      // Create new dashboard
      dashboardData.DashboardCode = `DASH-${Date.now()}`;
      response = await fetch(`${API_URL}/dashboards`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(dashboardData)
      });
    }

    if (!response.ok) throw new Error('Failed to save dashboard');
    
    const savedDashboard = await response.json();
    
    // Show success message
    alert(`Dashboard "${dashboardName}" saved successfully!`);
    
    // Show assignment modal
    setShowAssignModal(true);
    setDashboardCode(savedDashboard.DashboardCode);
    
    // If this is a new dashboard, redirect to the edit URL
    if (!dashboardCode) {
      navigate(`/dashboard-builder/${savedDashboard.DashboardCode}`, { replace: true });
    }
  } catch (error) {
    console.error('Error saving dashboard:', error);
    alert('Failed to save dashboard. Please try again.');
  } finally {
    setIsSaving(false);
  }
};

// Add this function to handle user assignments
const handleSaveAssignments = async () => {
  if (!dashboardCode) return;
  
  try {
    const token = localStorage.getItem('authToken') || '';
    
    // First, remove existing assignments
    await fetch(`${API_URL}/user-dashboards/dashboard/${dashboardCode}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    
    // Then add new assignments
    if (selectedUsers.length > 0) {
      const assignments = selectedUsers.map(userId => ({
        UserID: userId,
        DashboardCode: dashboardCode
      }));
      
      await fetch(`${API_URL}/user-dashboards`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(assignments)
      });
    }
    
    setShowAssignModal(false);
    alert('Dashboard assigned to users successfully!');
  } catch (error) {
    console.error('Error saving assignments:', error);
    alert('Failed to assign dashboard to users. Please try again.');
  }
};



  // Apply animation to element
  const applyAnimation = (animation) => {
    if (selectedId) {
      updateElement(selectedId, { animation });
      setAnimationType(animation);
    }
  };

  // Handle element selection
  const handleElementSelect = (id) => {
    setSelectedId(id);
    setRightPanelOpen(true);
    setLeftPanelOpen(false);
    
    const element = elements.find(el => el.id === id);
    if (element && (element.type === 'bar' || element.type === 'line' || element.type === 'scatter')) {
      // Get axis labels
      const getXAxisLabel = () => {
        if (element.type === 'scatter') {
          return element.chartConfig.groupBy || 'X-Axis';
        }
        return element.chartConfig.groupBy || 'Category';
      };
      
      const getYAxisLabel = () => {
        if (element.type === 'scatter') {
          return element.chartConfig.valueField || 'Y-Axis';
        }
        return element.chartConfig.valueField || 'Value';
      };

      // Set axis label properties from the element
      setXLabelProps(element.axisLabels?.x || { 
        x: 200, 
        y: 310, 
        rotation: 0, 
        color: '#000', 
        fontSize: 12, 
        fontFamily: 'Arial',
        text: getXAxisLabel()
      });
      setYLabelProps(element.axisLabels?.y || { 
        x: 20, 
        y: 150, 
        rotation: -90, 
        color: '#000', 
        fontSize: 12, 
        fontFamily: 'Arial',
        text: getYAxisLabel()
      });
      setEditingAxis(null);
      setShowXColorPicker(false);
      setShowYColorPicker(false);
    }
  };

  // Open left panel for adding new element
  const openAddElementPanel = () => {
    setLeftPanelOpen(true);
    setRightPanelOpen(false);
    setEditingAxis(null);
    setShowXColorPicker(false);
    setShowYColorPicker(false);
  };

  // Fetch views data from API
  const fetchViews = async () => {
    try {
      console.log('Fetching views...');
      const token = localStorage.getItem('authToken') || '';
      const response = await fetch(`${API_URL}/databaseViews`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch views: ${response.status}`);
      }

      const data = await response.json();
      console.log('Views data:', data);
      setViewsData(data);
    } catch (error) {
      console.error('Error fetching views:', error);
      setApiError('Failed to fetch views. Please try again.');
    }
  };

  // Fetch data when screen, view, or tables change
  useEffect(() => {
    const fetchData = async () => {
  const sourceName = sourceType === 'screen' ? selectedScreen : selectedView;
  
  // For screens, we need to check if at least one table is selected
  // For views, we can proceed directly
  if (sourceName && 
      (sourceType === 'view' || 
       (sourceType === 'screen' && (selectedTables.header || selectedTables.details)))) {
    setIsLoading(true);
    setApiError('');
    try {
      const data = await fetchDataFromAPI(sourceType, sourceName, selectedTables);
      setApiData(data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setApiError('Failed to fetch data. Please try again.');
      setApiData({ header: [], details: [] }); // Ensure apiData is always set
    } finally {
      setIsLoading(false);
    }
  } else {
    setApiData({ header: [], details: [] }); // Ensure apiData is always set
    setApiError('');
  }
};
    
    fetchData();
  }, [sourceType, selectedScreen, selectedView, selectedTables]);

  // Fetch views when component mounts
  useEffect(() => {
    fetchViews();
  }, []);

  // Update available columns when screen, view, or tables change
  useEffect(() => {
    const sourceName = sourceType === 'screen' ? selectedScreen : selectedView;
    
    if (sourceName) {
      let columns = [];
      
      if (sourceType === 'screen') {
        const screen = screensData.find(s => s.name === sourceName);
        if (screen) {
          // Get columns for header table
          if (screen.headerTable && tableColumns[screen.headerTable]) {
            columns = [...columns, ...tableColumns[screen.headerTable]];
          }
          
          // Get columns for details table
          if (screen.detailTable && tableColumns[screen.detailTable]) {
            columns = [...columns, ...tableColumns[screen.detailTable]];
          }
        }
      } else if (sourceType === 'view') {
        // For views, get columns from the viewsData
        if (viewsData[sourceName]) {
          columns = viewsData[sourceName].map(col => col.columnName);
        }
      }
      
      // If we have API data, extract columns from the actual data
      if (apiData.header.length > 0) {
        const sampleHeader = apiData.header[0];
        if (sampleHeader) {
          // Exclude nested arrays from column list
          const headerColumns = Object.keys(sampleHeader).filter(key => 
            !Array.isArray(sampleHeader[key])
          );
          columns = [...columns, ...headerColumns];
        }
      }
      
      if (apiData.details.length > 0) {
        const sampleDetail = apiData.details[0];
        if (sampleDetail) {
          columns = [...columns, ...Object.keys(sampleDetail)];
        }
      }
      
      // Remove duplicates
      const uniqueColumns = [...new Set(columns)];
      setAvailableColumns(uniqueColumns);
    } else {
      setAvailableColumns([]);
    }
  }, [sourceType, selectedScreen, selectedView, selectedTables, apiData, viewsData]);

  // Reset selections when source type changes
  useEffect(() => {
    setSelectedScreen('');
    setSelectedView('');
    setSelectedTables({ header: false, details: false });
    setSelectedColumns([]);
    setDateRange({ from: '', to: '' });
    setVisualizationType('');
    setSelectedUsers([]);
    // UPDATED: Reset chart config with valueField2
    setChartConfig({ groupBy: '', valueField: '', valueField2: '' });
    setFilters({});
    setApiError('');
    setEditingAxis(null);
    setShowXColorPicker(false);
    setShowYColorPicker(false);
  }, [sourceType]);

  // Reset selections when screen or view changes
  useEffect(() => {
    setSelectedTables({ header: false, details: false });
    setSelectedColumns([]);
    setDateRange({ from: '', to: '' });
    setVisualizationType('');
    setSelectedUsers([]);
    // UPDATED: Reset chart config with valueField2
    setChartConfig({ groupBy: '', valueField: '', valueField2: '' });
    setFilters({});
    setApiError('');
    setEditingAxis(null);
    setShowXColorPicker(false);
    setShowYColorPicker(false);
  }, [selectedScreen, selectedView]);

  // Auto-suggest visualization when columns change
  useEffect(() => {
    if (selectedColumns.length > 0) {
      const suggestedType = suggestVisualization(selectedColumns);
      setVisualizationType(suggestedType);
      
      // Auto-configure chart based on column types
      if (suggestedType !== 'table' && suggestedType !== 'text') {
        const columnTypes = selectedColumns.map(col => ({
          name: col,
          type: getColumnType(col)
        }));
        
        const categoricalColumns = columnTypes.filter(col => col.type === 'categorical').map(col => col.name);
        const numericColumns = columnTypes.filter(col => col.type === 'numeric').map(col => col.name);
        const dateColumns = columnTypes.filter(col => col.type === 'date').map(col => col.name);
        
        if (suggestedType === 'bar' || suggestedType === 'pie') {
          if (categoricalColumns.length > 0 && numericColumns.length > 0) {
            setChartConfig({
              groupBy: categoricalColumns[0],
              valueField: numericColumns[0],
              valueField2: numericColumns.length > 1 ? numericColumns[1] : ''
            });
          }
        } else if (suggestedType === 'line') {
          if (dateColumns.length > 0 && numericColumns.length > 0) {
            setChartConfig({
              groupBy: dateColumns[0],
              valueField: numericColumns[0],
              valueField2: numericColumns.length > 1 ? numericColumns[1] : ''
            });
          }
        } else if (suggestedType === 'scatter') {
          if (numericColumns.length >= 2) {
            setChartConfig({
              groupBy: numericColumns[0],
              valueField: numericColumns[1],
              valueField2: numericColumns.length > 2 ? numericColumns[2] : ''
            });
          }
        } else if (suggestedType === 'doubleline' || suggestedType === 'stackedarea' || suggestedType === 'stackbar' || suggestedType === 'composed') {
          if (categoricalColumns.length > 0 && numericColumns.length >= 2) {
            setChartConfig({
              groupBy: categoricalColumns[0],
              valueField: numericColumns[0],
              valueField2: numericColumns[1]
            });
          } else if (dateColumns.length > 0 && numericColumns.length >= 2) {
            setChartConfig({
              groupBy: dateColumns[0],
              valueField: numericColumns[0],
              valueField2: numericColumns[1]
            });
          }
        }
      }
    }
  }, [selectedColumns]);

  // Calculate container height based on elements
  const calculateContainerHeight = () => {
    if (elements.length === 0) {
      // If no elements, use full viewport height
      return '100vh';
    }
    
    // Find the bottom-most position of any element
    let maxBottom = 0;
    elements.forEach(element => {
      const bottom = element.y + element.height;
      if (bottom > maxBottom) {
        maxBottom = bottom;
      }
    });
    
    // Add extra space (100px) below the last element
    const extraSpace = 100;
    const calculatedHeight = maxBottom + extraSpace;
    
    // Get viewport height
    const viewportHeight = window.innerHeight;
    
    // Use the larger of calculated height or viewport height
    return `${Math.max(calculatedHeight, viewportHeight)}px`;
  };

  // Update container height when elements change or window resizes
  useEffect(() => {
    const updateHeight = () => {
      setContainerHeight(calculateContainerHeight());
    };
    
    // Initial height calculation
    updateHeight();
    
    // Add resize listener
    window.addEventListener('resize', updateHeight);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, [elements]);

  // Handle column selection
  const toggleColumnSelection = (column) => {
    if (selectedColumns.includes(column)) {
      setSelectedColumns(selectedColumns.filter(col => col !== column));
      // Remove filter for this column if it exists
      const newFilters = { ...filters };
      delete newFilters[column];
      setFilters(newFilters);
    } else {
      setSelectedColumns([...selectedColumns, column]);
    }
  };

  // Handle filter change
  const handleFilterChange = (column, value) => {
    setFilters({
      ...filters,
      [column]: value
    });
  };

  // Handle user selection
// Add this function to toggle user selection
const toggleUserSelection = (userId) => {
  if (selectedUsers.includes(userId)) {
    setSelectedUsers(selectedUsers.filter(id => id !== userId));
  } else {
    setSelectedUsers([...selectedUsers, userId]);
  }
};

  // Handle additional column selection in right panel
  const handleAdditionalColumnSelection = (elementId, column) => {
    setElements(elements.map(el => {
      if (el.id === elementId) {
        const newColumns = el.columns.includes(column) 
          ? el.columns.filter(col => col !== column)
          : [...el.columns, column];
        
        // Update data with new columns
        let newData = [...el.data];
        if (newColumns.length > el.columns.length) {
          // Adding a column - need to get the data for that column
          newData = newData.map(item => {
            const newItem = { ...item };
            if (item[column] === undefined) {
              newItem[column] = '';
            }
            return newItem;
          });
        } else {
          // Removing a column - need to filter out that column
          newData = newData.map(item => {
            const newItem = { ...item };
            delete newItem[column];
            return newItem;
          });
        }
        
        return {
          ...el,
          columns: newColumns,
          data: newData
        };
      }
      return el;
    }));
  };

  // Custom active shape for pie chart
  const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
    
    return (
      <g>
        <path d={`M${cx},${cy} L${cx + outerRadius * Math.cos(-startAngle * Math.PI / 180)},${cy + outerRadius * Math.sin(-startAngle * Math.PI / 180)} 
          A${outerRadius},${outerRadius} 0 0,1 ${cx + outerRadius * Math.cos(-endAngle * Math.PI / 180)},${cy + outerRadius * Math.sin(-endAngle * Math.PI / 180)} 
          Z`} 
          fill={fill} 
          stroke="#fff" 
          strokeWidth={2}
        />
        <path d={`M${cx},${cy} L${cx + innerRadius * Math.cos(-startAngle * Math.PI / 180)},${cy + innerRadius * Math.sin(-startAngle * Math.PI / 180)} 
          A${innerRadius},${innerRadius} 0 0,0 ${cx + innerRadius * Math.cos(-endAngle * Math.PI / 180)},${cy + innerRadius * Math.sin(-endAngle * Math.PI / 180)} 
          Z`} 
          fill="#fff" 
          stroke={fill} 
          strokeWidth={2}
        />
      </g>
    );
  };

  // Render element based on its type
  const renderElement = (element) => {
    const isSelected = element.id === selectedId;
    
    const style = {
      border: isSelected ? '2px solid #3b82f6' : '1px solid #d1d5db',
      borderRadius: '4px',
      backgroundColor: element.backgroundColor || '#ffffff',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden',
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    };

    // Render title if it exists
    const renderTitle = () => {
      if (!element.title) return null;
      
      const alignmentStyle = {
        textAlign: element.titleAlignment || 'center',
        padding: '8px',
        fontWeight: 'bold',
        fontSize: `${element.titleFontSize || 14}px`,
        color: element.titleColor || '#000000',
        borderBottom: '1px solid #e5e7eb',
        paddingLeft: `${element.titlePadding || 0}px`,
        paddingRight: `${element.titlePadding || 0}px`
      };
      
      return (
        <div style={alignmentStyle}>
          {element.title}
        </div>
      );
    };

    if (element.type === 'text') {
      return (
        <div style={style}>
          {renderTitle()}
          <div
            style={{
              fontSize: `${element.fontSize}px`,
              fontFamily: element.fontFamily,
              color: element.fill,
              padding: '10px',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center'
            }}
          >
            {element.text}
          </div>
        </div>
      );
    }
    
    if (element.type === 'table') {
      return (
        <div style={style}>
          {renderTitle()}
          <div className="overflow-auto h-full">
            {element.data && element.data.length > 0 ? (
              <table className="min-w-full">
                <thead style={{ backgroundColor: element.tableHeaderColor || '#f3f4f6' }}>
                  <tr>
                    {element.columns.map((key, index) => (
                      <th key={index} className="py-2 px-3 border-b text-left capitalize">{key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {element.data.map((row, rowIndex) => (
                    <tr 
                      key={rowIndex} 
                      style={{ 
                        backgroundColor: rowIndex % 2 === 0 
                          ? (element.tableRowColor || '#ffffff') 
                          : (element.tableAltRowColor || '#f9fafb')
                      }}
                    >
                      {element.columns.map((key, colIndex) => (
                        <td key={colIndex} className="py-2 px-3 border-b">{row[key]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                No data available. Please check your filters or try refreshing the data.
              </div>
            )}
          </div>
        </div>
      );
    }
    
    // Prepare data for charts
    let chartData = [];
    
    if (element.data && element.data.length > 0) {
      console.log('Preparing chart data for element type:', element.type);
      console.log('Chart config:', element.chartConfig);
      
      if (element.chartConfig.groupBy && element.chartConfig.valueField) {
        // UPDATED: Pass valueField2 to aggregation function
        chartData = aggregateDataForChart(
          element.data, 
          element.type, 
          element.chartConfig.groupBy, 
          element.chartConfig.valueField,
          element.chartConfig.valueField2
        );
      } else {
        // Default to first two columns if no config
        const firstCol = element.columns[0] || 'name';
        const secondCol = element.columns[1] || 'value';
        const thirdCol = element.columns[2] || 'value2';
        chartData = element.data.map(item => ({
          name: item[firstCol],
          value: parseFloat(item[secondCol]) || 0,
          value2: parseFloat(item[thirdCol]) || 0
        }));
      }
    }
    
    console.log('Final chart data:', chartData);
    
    // Get axis labels
    const getXAxisLabel = () => {
      if (element.type === 'scatter') {
        return element.chartConfig.groupBy || 'X-Axis';
      }
      return element.chartConfig.groupBy || 'Category';
    };
    
    const getYAxisLabel = () => {
      if (element.type === 'scatter') {
        return element.chartConfig.valueField || 'Y-Axis';
      }
      return element.chartConfig.valueField || 'Value';
    };
    
    // Get axis label properties
    const xAxisLabelProps = element.axisLabels?.x || { 
      x: 200, 
      y: 310, 
      rotation: 0, 
      color: '#000', 
      fontSize: 12, 
      fontFamily: 'Arial',
      text: getXAxisLabel()
    };
    const yAxisLabelProps = element.axisLabels?.y || { 
      x: 20, 
      y: 150, 
      rotation: -90, 
      color: '#000', 
      fontSize: 12, 
      fontFamily: 'Arial',
      text: getYAxisLabel()
    };
    
    return (
      <div style={style}>
        {renderTitle()}
        <div className="h-full w-full p-2">
          {chartData.length > 0 ? (
            <>
              {/* Bar Chart */}
              {element.type === 'bar' && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      label={
                        <DraggableLabel
                          value={xAxisLabelProps.text}
                          x={xAxisLabelProps.x}
                          y={xAxisLabelProps.y}
                          rotation={xAxisLabelProps.rotation}
                          color={xAxisLabelProps.color}
                          fontSize={xAxisLabelProps.fontSize}
                          fontFamily={xAxisLabelProps.fontFamily}
                          onPositionChange={(pos) => updateAxisLabel('x', pos)}
                          onRotationChange={(rotation) => updateAxisLabel('x', { rotation })}
                          onColorChange={(color) => updateAxisLabel('x', { color })}
                          onFontSizeChange={(fontSize) => updateAxisLabel('x', { fontSize })}
                          onFontFamilyChange={(fontFamily) => updateAxisLabel('x', { fontFamily })}
                          onTextChange={(text) => updateAxisLabel('x', { text })}
                          isEditing={editingAxis === 'x' && isSelected}
                          onEditStart={() => setEditingAxis('x')}
                          onEditEnd={() => setEditingAxis(null)}
                        />
                      }
                    />
                    <YAxis
                      label={
                        <DraggableLabel
                          value={yAxisLabelProps.text}
                          x={yAxisLabelProps.x}
                          y={yAxisLabelProps.y}
                          rotation={yAxisLabelProps.rotation}
                          color={yAxisLabelProps.color}
                          fontSize={yAxisLabelProps.fontSize}
                          fontFamily={yAxisLabelProps.fontFamily}
                          onPositionChange={(pos) => updateAxisLabel('y', pos)}
                          onRotationChange={(rotation) => updateAxisLabel('y', { rotation })}
                          onColorChange={(color) => updateAxisLabel('y', { color })}
                          onFontSizeChange={(fontSize) => updateAxisLabel('y', { fontSize })}
                          onFontFamilyChange={(fontFamily) => updateAxisLabel('y', { fontFamily })}
                          onTextChange={(text) => updateAxisLabel('y', { text })}
                          isEditing={editingAxis === 'y' && isSelected}
                          onEditStart={() => setEditingAxis('y')}
                          onEditEnd={() => setEditingAxis(null)}
                        />
                      }
                    />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="value" fill={element.fill || COLORS[0]} name={getYAxisLabel()} />
                  </BarChart>
                </ResponsiveContainer>
              )}
              
              {/* Line Chart */}
              {element.type === 'line' && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      label={
                        <DraggableLabel
                          value={xAxisLabelProps.text}
                          x={xAxisLabelProps.x}
                          y={xAxisLabelProps.y}
                          rotation={xAxisLabelProps.rotation}
                          color={xAxisLabelProps.color}
                          fontSize={xAxisLabelProps.fontSize}
                          fontFamily={xAxisLabelProps.fontFamily}
                          onPositionChange={(pos) => updateAxisLabel('x', pos)}
                          onRotationChange={(rotation) => updateAxisLabel('x', { rotation })}
                          onColorChange={(color) => updateAxisLabel('x', { color })}
                          onFontSizeChange={(fontSize) => updateAxisLabel('x', { fontSize })}
                          onFontFamilyChange={(fontFamily) => updateAxisLabel('x', { fontFamily })}
                          onTextChange={(text) => updateAxisLabel('x', { text })}
                          isEditing={editingAxis === 'x' && isSelected}
                          onEditStart={() => setEditingAxis('x')}
                          onEditEnd={() => setEditingAxis(null)}
                        />
                      }
                    />
                    <YAxis
                      label={
                        <DraggableLabel
                          value={yAxisLabelProps.text}
                          x={yAxisLabelProps.x}
                          y={yAxisLabelProps.y}
                          rotation={yAxisLabelProps.rotation}
                          color={yAxisLabelProps.color}
                          fontSize={yAxisLabelProps.fontSize}
                          fontFamily={yAxisLabelProps.fontFamily}
                          onPositionChange={(pos) => updateAxisLabel('y', pos)}
                          onRotationChange={(rotation) => updateAxisLabel('y', { rotation })}
                          onColorChange={(color) => updateAxisLabel('y', { color })}
                          onFontSizeChange={(fontSize) => updateAxisLabel('y', { fontSize })}
                          onFontFamilyChange={(fontFamily) => updateAxisLabel('y', { fontFamily })}
                          onTextChange={(text) => updateAxisLabel('y', { text })}
                          isEditing={editingAxis === 'y' && isSelected}
                          onEditStart={() => setEditingAxis('y')}
                          onEditEnd={() => setEditingAxis(null)}
                        />
                      }
                    />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="value" stroke={element.fill || COLORS[0]} activeDot={{ r: 8 }} name={getYAxisLabel()} />
                  </LineChart>
                </ResponsiveContainer>
              )}
              
              {/* Double Line Chart - UPDATED */}
              {element.type === 'doubleline' && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      label={
                        <DraggableLabel
                          value={xAxisLabelProps.text}
                          x={xAxisLabelProps.x}
                          y={xAxisLabelProps.y}
                          rotation={xAxisLabelProps.rotation}
                          color={xAxisLabelProps.color}
                          fontSize={xAxisLabelProps.fontSize}
                          fontFamily={xAxisLabelProps.fontFamily}
                          onPositionChange={(pos) => updateAxisLabel('x', pos)}
                          onRotationChange={(rotation) => updateAxisLabel('x', { rotation })}
                          onColorChange={(color) => updateAxisLabel('x', { color })}
                          onFontSizeChange={(fontSize) => updateAxisLabel('x', { fontSize })}
                          onFontFamilyChange={(fontFamily) => updateAxisLabel('x', { fontFamily })}
                          onTextChange={(text) => updateAxisLabel('x', { text })}
                          isEditing={editingAxis === 'x' && isSelected}
                          onEditStart={() => setEditingAxis('x')}
                          onEditEnd={() => setEditingAxis(null)}
                        />
                      }
                    />
                    <YAxis
                      label={
                        <DraggableLabel
                          value={yAxisLabelProps.text}
                          x={yAxisLabelProps.x}
                          y={yAxisLabelProps.y}
                          rotation={yAxisLabelProps.rotation}
                          color={yAxisLabelProps.color}
                          fontSize={yAxisLabelProps.fontSize}
                          fontFamily={yAxisLabelProps.fontFamily}
                          onPositionChange={(pos) => updateAxisLabel('y', pos)}
                          onRotationChange={(rotation) => updateAxisLabel('y', { rotation })}
                          onColorChange={(color) => updateAxisLabel('y', { color })}
                          onFontSizeChange={(fontSize) => updateAxisLabel('y', { fontSize })}
                          onFontFamilyChange={(fontFamily) => updateAxisLabel('y', { fontFamily })}
                          onTextChange={(text) => updateAxisLabel('y', { text })}
                          isEditing={editingAxis === 'y' && isSelected}
                          onEditStart={() => setEditingAxis('y')}
                          onEditEnd={() => setEditingAxis(null)}
                        />
                      }
                    />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="value" stroke={element.fill || COLORS[0]} activeDot={{ r: 8 }} name={getYAxisLabel()} />
                    <Line type="monotone" dataKey="value2" stroke={element.secondaryValueColor || COLORS[1]} activeDot={{ r: 8 }} name={element.chartConfig.valueField2 || 'Secondary Value'} />
                  </LineChart>
                </ResponsiveContainer>
              )}
              
              {/* Dashed Line Chart */}
              {element.type === 'dashedline' && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      label={
                        <DraggableLabel
                          value={xAxisLabelProps.text}
                          x={xAxisLabelProps.x}
                          y={xAxisLabelProps.y}
                          rotation={xAxisLabelProps.rotation}
                          color={xAxisLabelProps.color}
                          fontSize={xAxisLabelProps.fontSize}
                          fontFamily={xAxisLabelProps.fontFamily}
                          onPositionChange={(pos) => updateAxisLabel('x', pos)}
                          onRotationChange={(rotation) => updateAxisLabel('x', { rotation })}
                          onColorChange={(color) => updateAxisLabel('x', { color })}
                          onFontSizeChange={(fontSize) => updateAxisLabel('x', { fontSize })}
                          onFontFamilyChange={(fontFamily) => updateAxisLabel('x', { fontFamily })}
                          onTextChange={(text) => updateAxisLabel('x', { text })}
                          isEditing={editingAxis === 'x' && isSelected}
                          onEditStart={() => setEditingAxis('x')}
                          onEditEnd={() => setEditingAxis(null)}
                        />
                      }
                    />
                    <YAxis
                      label={
                        <DraggableLabel
                          value={yAxisLabelProps.text}
                          x={yAxisLabelProps.x}
                          y={yAxisLabelProps.y}
                          rotation={yAxisLabelProps.rotation}
                          color={yAxisLabelProps.color}
                          fontSize={yAxisLabelProps.fontSize}
                          fontFamily={yAxisLabelProps.fontFamily}
                          onPositionChange={(pos) => updateAxisLabel('y', pos)}
                          onRotationChange={(rotation) => updateAxisLabel('y', { rotation })}
                          onColorChange={(color) => updateAxisLabel('y', { color })}
                          onFontSizeChange={(fontSize) => updateAxisLabel('y', { fontSize })}
                          onFontFamilyChange={(fontFamily) => updateAxisLabel('y', { fontFamily })}
                          onTextChange={(text) => updateAxisLabel('y', { text })}
                          isEditing={editingAxis === 'y' && isSelected}
                          onEditStart={() => setEditingAxis('y')}
                          onEditEnd={() => setEditingAxis(null)}
                        />
                      }
                    />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="value" stroke={element.fill || COLORS[0]} strokeDasharray="5 5" activeDot={{ r: 8 }} name={getYAxisLabel()} />
                  </LineChart>
                </ResponsiveContainer>
              )}
              
              {/* Area Chart */}
              {element.type === 'area' && (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      label={
                        <DraggableLabel
                          value={xAxisLabelProps.text}
                          x={xAxisLabelProps.x}
                          y={xAxisLabelProps.y}
                          rotation={xAxisLabelProps.rotation}
                          color={xAxisLabelProps.color}
                          fontSize={xAxisLabelProps.fontSize}
                          fontFamily={xAxisLabelProps.fontFamily}
                          onPositionChange={(pos) => updateAxisLabel('x', pos)}
                          onRotationChange={(rotation) => updateAxisLabel('x', { rotation })}
                          onColorChange={(color) => updateAxisLabel('x', { color })}
                          onFontSizeChange={(fontSize) => updateAxisLabel('x', { fontSize })}
                          onFontFamilyChange={(fontFamily) => updateAxisLabel('x', { fontFamily })}
                          onTextChange={(text) => updateAxisLabel('x', { text })}
                          isEditing={editingAxis === 'x' && isSelected}
                          onEditStart={() => setEditingAxis('x')}
                          onEditEnd={() => setEditingAxis(null)}
                        />
                      }
                    />
                    <YAxis
                      label={
                        <DraggableLabel
                          value={yAxisLabelProps.text}
                          x={yAxisLabelProps.x}
                          y={yAxisLabelProps.y}
                          rotation={yAxisLabelProps.rotation}
                          color={yAxisLabelProps.color}
                          fontSize={yAxisLabelProps.fontSize}
                          fontFamily={yAxisLabelProps.fontFamily}
                          onPositionChange={(pos) => updateAxisLabel('y', pos)}
                          onRotationChange={(rotation) => updateAxisLabel('y', { rotation })}
                          onColorChange={(color) => updateAxisLabel('y', { color })}
                          onFontSizeChange={(fontSize) => updateAxisLabel('y', { fontSize })}
                          onFontFamilyChange={(fontFamily) => updateAxisLabel('y', { fontFamily })}
                          onTextChange={(text) => updateAxisLabel('y', { text })}
                          isEditing={editingAxis === 'y' && isSelected}
                          onEditStart={() => setEditingAxis('y')}
                          onEditEnd={() => setEditingAxis(null)}
                        />
                      }
                    />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="value" stroke={element.fill || COLORS[0]} fill={`${element.fill || COLORS[0]}40`} name={getYAxisLabel()} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
              
              {/* Stacked Area Chart - UPDATED */}
              {element.type === 'stackedarea' && (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      label={
                        <DraggableLabel
                          value={xAxisLabelProps.text}
                          x={xAxisLabelProps.x}
                          y={xAxisLabelProps.y}
                          rotation={xAxisLabelProps.rotation}
                          color={xAxisLabelProps.color}
                          fontSize={xAxisLabelProps.fontSize}
                          fontFamily={xAxisLabelProps.fontFamily}
                          onPositionChange={(pos) => updateAxisLabel('x', pos)}
                          onRotationChange={(rotation) => updateAxisLabel('x', { rotation })}
                          onColorChange={(color) => updateAxisLabel('x', { color })}
                          onFontSizeChange={(fontSize) => updateAxisLabel('x', { fontSize })}
                          onFontFamilyChange={(fontFamily) => updateAxisLabel('x', { fontFamily })}
                          onTextChange={(text) => updateAxisLabel('x', { text })}
                          isEditing={editingAxis === 'x' && isSelected}
                          onEditStart={() => setEditingAxis('x')}
                          onEditEnd={() => setEditingAxis(null)}
                        />
                      }
                    />
                    <YAxis
                      label={
                        <DraggableLabel
                          value={yAxisLabelProps.text}
                          x={yAxisLabelProps.x}
                          y={yAxisLabelProps.y}
                          rotation={yAxisLabelProps.rotation}
                          color={yAxisLabelProps.color}
                          fontSize={yAxisLabelProps.fontSize}
                          fontFamily={yAxisLabelProps.fontFamily}
                          onPositionChange={(pos) => updateAxisLabel('y', pos)}
                          onRotationChange={(rotation) => updateAxisLabel('y', { rotation })}
                          onColorChange={(color) => updateAxisLabel('y', { color })}
                          onFontSizeChange={(fontSize) => updateAxisLabel('y', { fontSize })}
                          onFontFamilyChange={(fontFamily) => updateAxisLabel('y', { fontFamily })}
                          onTextChange={(text) => updateAxisLabel('y', { text })}
                          isEditing={editingAxis === 'y' && isSelected}
                          onEditStart={() => setEditingAxis('y')}
                          onEditEnd={() => setEditingAxis(null)}
                        />
                      }
                    />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="value" stackId="1" stroke={element.fill || COLORS[0]} fill={`${element.fill || COLORS[0]}40`} name={getYAxisLabel()} />
                    <Area type="monotone" dataKey="value2" stackId="1" stroke={element.secondaryValueColor || COLORS[1]} fill={`${element.secondaryValueColor || COLORS[1]}40`} name={element.chartConfig.valueField2 || 'Secondary Value'} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
              
              {/* Cardinal Area Chart */}
              {element.type === 'cardinalarea' && (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      label={
                        <DraggableLabel
                          value={xAxisLabelProps.text}
                          x={xAxisLabelProps.x}
                          y={xAxisLabelProps.y}
                          rotation={xAxisLabelProps.rotation}
                          color={xAxisLabelProps.color}
                          fontSize={xAxisLabelProps.fontSize}
                          fontFamily={xAxisLabelProps.fontFamily}
                          onPositionChange={(pos) => updateAxisLabel('x', pos)}
                          onRotationChange={(rotation) => updateAxisLabel('x', { rotation })}
                          onColorChange={(color) => updateAxisLabel('x', { color })}
                          onFontSizeChange={(fontSize) => updateAxisLabel('x', { fontSize })}
                          onFontFamilyChange={(fontFamily) => updateAxisLabel('x', { fontFamily })}
                          onTextChange={(text) => updateAxisLabel('x', { text })}
                          isEditing={editingAxis === 'x' && isSelected}
                          onEditStart={() => setEditingAxis('x')}
                          onEditEnd={() => setEditingAxis(null)}
                        />
                      }
                    />
                    <YAxis
                      label={
                        <DraggableLabel
                          value={yAxisLabelProps.text}
                          x={yAxisLabelProps.x}
                          y={yAxisLabelProps.y}
                          rotation={yAxisLabelProps.rotation}
                          color={yAxisLabelProps.color}
                          fontSize={yAxisLabelProps.fontSize}
                          fontFamily={yAxisLabelProps.fontFamily}
                          onPositionChange={(pos) => updateAxisLabel('y', pos)}
                          onRotationChange={(rotation) => updateAxisLabel('y', { rotation })}
                          onColorChange={(color) => updateAxisLabel('y', { color })}
                          onFontSizeChange={(fontSize) => updateAxisLabel('y', { fontSize })}
                          onFontFamilyChange={(fontFamily) => updateAxisLabel('y', { fontFamily })}
                          onTextChange={(text) => updateAxisLabel('y', { text })}
                          isEditing={editingAxis === 'y' && isSelected}
                          onEditStart={() => setEditingAxis('y')}
                          onEditEnd={() => setEditingAxis(null)}
                        />
                      }
                    />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="value" stroke={element.fill || COLORS[0]} fill={`${element.fill || COLORS[0]}40`} name={getYAxisLabel()} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
              
              {/* Stack Bar Chart - UPDATED */}
              {element.type === 'stackbar' && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      label={
                        <DraggableLabel
                          value={xAxisLabelProps.text}
                          x={xAxisLabelProps.x}
                          y={xAxisLabelProps.y}
                          rotation={xAxisLabelProps.rotation}
                          color={xAxisLabelProps.color}
                          fontSize={xAxisLabelProps.fontSize}
                          fontFamily={xAxisLabelProps.fontFamily}
                          onPositionChange={(pos) => updateAxisLabel('x', pos)}
                          onRotationChange={(rotation) => updateAxisLabel('x', { rotation })}
                          onColorChange={(color) => updateAxisLabel('x', { color })}
                          onFontSizeChange={(fontSize) => updateAxisLabel('x', { fontSize })}
                          onFontFamilyChange={(fontFamily) => updateAxisLabel('x', { fontFamily })}
                          onTextChange={(text) => updateAxisLabel('x', { text })}
                          isEditing={editingAxis === 'x' && isSelected}
                          onEditStart={() => setEditingAxis('x')}
                          onEditEnd={() => setEditingAxis(null)}
                        />
                      }
                    />
                    <YAxis
                      label={
                        <DraggableLabel
                          value={yAxisLabelProps.text}
                          x={yAxisLabelProps.x}
                          y={yAxisLabelProps.y}
                          rotation={yAxisLabelProps.rotation}
                          color={yAxisLabelProps.color}
                          fontSize={yAxisLabelProps.fontSize}
                          fontFamily={yAxisLabelProps.fontFamily}
                          onPositionChange={(pos) => updateAxisLabel('y', pos)}
                          onRotationChange={(rotation) => updateAxisLabel('y', { rotation })}
                          onColorChange={(color) => updateAxisLabel('y', { color })}
                          onFontSizeChange={(fontSize) => updateAxisLabel('y', { fontSize })}
                          onFontFamilyChange={(fontFamily) => updateAxisLabel('y', { fontFamily })}
                          onTextChange={(text) => updateAxisLabel('y', { text })}
                          isEditing={editingAxis === 'y' && isSelected}
                          onEditStart={() => setEditingAxis('y')}
                          onEditEnd={() => setEditingAxis(null)}
                        />
                      }
                    />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="value" stackId="a" fill={element.fill || COLORS[0]} name={getYAxisLabel()} />
                    <Bar dataKey="value2" stackId="a" fill={element.secondaryValueColor || COLORS[1]} name={element.chartConfig.valueField2 || 'Secondary Value'} />
                  </BarChart>
                </ResponsiveContainer>
              )}
              
              {/* Composed Chart - UPDATED */}
              {element.type === 'composed' && (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      label={
                        <DraggableLabel
                          value={xAxisLabelProps.text}
                          x={xAxisLabelProps.x}
                          y={xAxisLabelProps.y}
                          rotation={xAxisLabelProps.rotation}
                          color={xAxisLabelProps.color}
                          fontSize={xAxisLabelProps.fontSize}
                          fontFamily={xAxisLabelProps.fontFamily}
                          onPositionChange={(pos) => updateAxisLabel('x', pos)}
                          onRotationChange={(rotation) => updateAxisLabel('x', { rotation })}
                          onColorChange={(color) => updateAxisLabel('x', { color })}
                          onFontSizeChange={(fontSize) => updateAxisLabel('x', { fontSize })}
                          onFontFamilyChange={(fontFamily) => updateAxisLabel('x', { fontFamily })}
                          onTextChange={(text) => updateAxisLabel('x', { text })}
                          isEditing={editingAxis === 'x' && isSelected}
                          onEditStart={() => setEditingAxis('x')}
                          onEditEnd={() => setEditingAxis(null)}
                        />
                      }
                    />
                    <YAxis
                      label={
                        <DraggableLabel
                          value={yAxisLabelProps.text}
                          x={yAxisLabelProps.x}
                          y={yAxisLabelProps.y}
                          rotation={yAxisLabelProps.rotation}
                          color={yAxisLabelProps.color}
                          fontSize={yAxisLabelProps.fontSize}
                          fontFamily={yAxisLabelProps.fontFamily}
                          onPositionChange={(pos) => updateAxisLabel('y', pos)}
                          onRotationChange={(rotation) => updateAxisLabel('y', { rotation })}
                          onColorChange={(color) => updateAxisLabel('y', { color })}
                          onFontSizeChange={(fontSize) => updateAxisLabel('y', { fontSize })}
                          onFontFamilyChange={(fontFamily) => updateAxisLabel('y', { fontFamily })}
                          onTextChange={(text) => updateAxisLabel('y', { text })}
                          isEditing={editingAxis === 'y' && isSelected}
                          onEditStart={() => setEditingAxis('y')}
                          onEditEnd={() => setEditingAxis(null)}
                        />
                      }
                    />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="value" fill={element.fill || COLORS[0]} name={getYAxisLabel()} />
                    <Line type="monotone" dataKey="value2" stroke={element.secondaryValueColor || COLORS[1]} activeDot={{ r: 8 }} name={element.chartConfig.valueField2 || 'Secondary Value'} />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
              
              {/* Same Data Composed Chart - UPDATED */}
              {element.type === 'samedatacomposed' && (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      label={
                        <DraggableLabel
                          value={xAxisLabelProps.text}
                          x={xAxisLabelProps.x}
                          y={xAxisLabelProps.y}
                          rotation={xAxisLabelProps.rotation}
                          color={xAxisLabelProps.color}
                          fontSize={xAxisLabelProps.fontSize}
                          fontFamily={xAxisLabelProps.fontFamily}
                          onPositionChange={(pos) => updateAxisLabel('x', pos)}
                          onRotationChange={(rotation) => updateAxisLabel('x', { rotation })}
                          onColorChange={(color) => updateAxisLabel('x', { color })}
                          onFontSizeChange={(fontSize) => updateAxisLabel('x', { fontSize })}
                          onFontFamilyChange={(fontFamily) => updateAxisLabel('x', { fontFamily })}
                          onTextChange={(text) => updateAxisLabel('x', { text })}
                          isEditing={editingAxis === 'x' && isSelected}
                          onEditStart={() => setEditingAxis('x')}
                          onEditEnd={() => setEditingAxis(null)}
                        />
                      }
                    />
                    <YAxis
                      label={
                        <DraggableLabel
                          value={yAxisLabelProps.text}
                          x={yAxisLabelProps.x}
                          y={yAxisLabelProps.y}
                          rotation={yAxisLabelProps.rotation}
                          color={yAxisLabelProps.color}
                          fontSize={yAxisLabelProps.fontSize}
                          fontFamily={yAxisLabelProps.fontFamily}
                          onPositionChange={(pos) => updateAxisLabel('y', pos)}
                          onRotationChange={(rotation) => updateAxisLabel('y', { rotation })}
                          onColorChange={(color) => updateAxisLabel('y', { color })}
                          onFontSizeChange={(fontSize) => updateAxisLabel('y', { fontSize })}
                          onFontFamilyChange={(fontFamily) => updateAxisLabel('y', { fontFamily })}
                          onTextChange={(text) => updateAxisLabel('y', { text })}
                          isEditing={editingAxis === 'y' && isSelected}
                          onEditStart={() => setEditingAxis('y')}
                          onEditEnd={() => setEditingAxis(null)}
                        />
                      }
                    />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="value" fill={element.fill || COLORS[0]} name={getYAxisLabel()} />
                    <Line type="monotone" dataKey="value" stroke={element.secondaryValueColor || COLORS[1]} activeDot={{ r: 8 }} name="Trend" />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
              
              {/* Pie Chart */}
              {element.type === 'pie' && (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      outerRadius={80}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                    <text 
                      x="50%" 
                      y="95%" 
                      textAnchor="middle" 
                      fill={xAxisLabelProps.color}
                      fontSize={xAxisLabelProps.fontSize}
                      fontFamily={xAxisLabelProps.fontFamily}
                      transform={`rotate(${xAxisLabelProps.rotation}, 250, 290)`}
                      style={{ cursor: 'grab' }}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setEditingAxis('x');
                      }}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setEditingAxis('x');
                      }}
                    >
                      {xAxisLabelProps.text}
                    </text>
                  </PieChart>
                </ResponsiveContainer>
              )}
              
              {/* Two Level Pie Chart */}
              {element.type === 'twolevelpie' && (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={60}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[(index + 2) % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                    <text 
                      x="50%" 
                      y="95%" 
                      textAnchor="middle" 
                      fill={xAxisLabelProps.color}
                      fontSize={xAxisLabelProps.fontSize}
                      fontFamily={xAxisLabelProps.fontFamily}
                      transform={`rotate(${xAxisLabelProps.rotation}, 250, 290)`}
                      style={{ cursor: 'grab' }}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setEditingAxis('x');
                      }}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setEditingAxis('x');
                      }}
                    >
                      {xAxisLabelProps.text}
                    </text>
                  </PieChart>
                </ResponsiveContainer>
              )}
              
              {/* Straight Angle Pie Chart */}
              {element.type === 'straightanglepie' && (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      startAngle={180}
                      endAngle={0}
                      innerRadius={60}
                      outerRadius={80}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                    <text 
                      x="50%" 
                      y="95%" 
                      textAnchor="middle" 
                      fill={xAxisLabelProps.color}
                      fontSize={xAxisLabelProps.fontSize}
                      fontFamily={xAxisLabelProps.fontFamily}
                      transform={`rotate(${xAxisLabelProps.rotation}, 250, 290)`}
                      style={{ cursor: 'grab' }}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setEditingAxis('x');
                      }}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setEditingAxis('x');
                      }}
                    >
                      {xAxisLabelProps.text}
                    </text>
                  </PieChart>
                </ResponsiveContainer>
              )}
              
              {/* Custom Active Shape Pie Chart */}
              {element.type === 'customactiveshape' && (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      activeIndex={0}
                      activeShape={renderActiveShape}
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                    <text 
                      x="50%" 
                      y="95%" 
                      textAnchor="middle" 
                      fill={xAxisLabelProps.color}
                      fontSize={xAxisLabelProps.fontSize}
                      fontFamily={xAxisLabelProps.fontFamily}
                      transform={`rotate(${xAxisLabelProps.rotation}, 250, 290)`}
                      style={{ cursor: 'grab' }}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setEditingAxis('x');
                      }}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setEditingAxis('x');
                      }}
                    >
                      {xAxisLabelProps.text}
                    </text>
                  </PieChart>
                </ResponsiveContainer>
              )}
              
              {/* Pie Chart with Gap Rounded Corners */}
              {element.type === 'gaproundedcorners' && (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      cornerRadius={10}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                    <text 
                      x="50%" 
                      y="95%" 
                      textAnchor="middle" 
                      fill={xAxisLabelProps.color}
                      fontSize={xAxisLabelProps.fontSize}
                      fontFamily={xAxisLabelProps.fontFamily}
                      transform={`rotate(${xAxisLabelProps.rotation}, 250, 290)`}
                      style={{ cursor: 'grab' }}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setEditingAxis('x');
                      }}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setEditingAxis('x');
                      }}
                    >
                      {xAxisLabelProps.text}
                    </text>
                  </PieChart>
                </ResponsiveContainer>
              )}
              
              {/* Scatter Plot */}
              {element.type === 'scatter' && (
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      type="number" 
                      dataKey="x" 
                      name={getXAxisLabel()} 
                      label={
                        <DraggableLabel
                          value={xAxisLabelProps.text}
                          x={xAxisLabelProps.x}
                          y={xAxisLabelProps.y}
                          rotation={xAxisLabelProps.rotation}
                          color={xAxisLabelProps.color}
                          fontSize={xAxisLabelProps.fontSize}
                          fontFamily={xAxisLabelProps.fontFamily}
                          onPositionChange={(pos) => updateAxisLabel('x', pos)}
                          onRotationChange={(rotation) => updateAxisLabel('x', { rotation })}
                          onColorChange={(color) => updateAxisLabel('x', { color })}
                          onFontSizeChange={(fontSize) => updateAxisLabel('x', { fontSize })}
                          onFontFamilyChange={(fontFamily) => updateAxisLabel('x', { fontFamily })}
                          onTextChange={(text) => updateAxisLabel('x', { text })}
                          isEditing={editingAxis === 'x' && isSelected}
                          onEditStart={() => setEditingAxis('x')}
                          onEditEnd={() => setEditingAxis(null)}
                        />
                      }
                    />
                    <YAxis 
                      type="number" 
                      dataKey="y" 
                      name={getYAxisLabel()} 
                      label={
                        <DraggableLabel
                          value={yAxisLabelProps.text}
                          x={yAxisLabelProps.x}
                          y={yAxisLabelProps.y}
                          rotation={yAxisLabelProps.rotation}
                          color={yAxisLabelProps.color}
                          fontSize={yAxisLabelProps.fontSize}
                          fontFamily={yAxisLabelProps.fontFamily}
                          onPositionChange={(pos) => updateAxisLabel('y', pos)}
                          onRotationChange={(rotation) => updateAxisLabel('y', { rotation })}
                          onColorChange={(color) => updateAxisLabel('y', { color })}
                          onFontSizeChange={(fontSize) => updateAxisLabel('y', { fontSize })}
                          onFontFamilyChange={(fontFamily) => updateAxisLabel('y', { fontFamily })}
                          onTextChange={(text) => updateAxisLabel('y', { text })}
                          isEditing={editingAxis === 'y' && isSelected}
                          onEditStart={() => setEditingAxis('y')}
                          onEditEnd={() => setEditingAxis(null)}
                        />
                      }
                    />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                    <Legend />
                    <Scatter name="Data Points" data={chartData} fill={element.fill || COLORS[0]} />
                  </ScatterChart>
                </ResponsiveContainer>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              No data available for chart. Please check your filters or try refreshing the data.
              <div className="text-xs mt-2">
                Debug: Chart config: {JSON.stringify(element.chartConfig)}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Check if we should show column selection and other options
  const shouldShowColumnOptions = () => {
    if (sourceType === 'view' && selectedView) {
      return true;
    }
    
    if (sourceType === 'screen' && selectedScreen) {
      // For screens, we need to check if we have available columns
      // This will be true once we fetch the data or if we have columns from the config
      return availableColumns.length > 0;
    }
    
    return false;
  };

  // Get available columns for the selected element
  const getAvailableColumnsForElement = (element) => {
    if (element.sourceType === 'view') {
      const viewName = element.sourceName;
      if (viewsData[viewName]) {
        return viewsData[viewName].map(col => col.columnName);
      }
    } else if (element.sourceType === 'screen') {
      const screen = screensData.find(s => s.name === element.sourceName);
      if (screen) {
        let columns = [];
        if (screen.headerTable && tableColumns[screen.headerTable]) {
          columns = [...columns, ...tableColumns[screen.headerTable]];
        }
        if (screen.detailTable && tableColumns[screen.detailTable]) {
          columns = [...columns, ...tableColumns[screen.detailTable]];
        }
        return columns;
      }
    }
    return [];
  };

  // Figma-style input component
  const FigmaInput = ({ value, onChange, unit = 'px' }) => {
    const [inputValue, setInputValue] = useState(value);
    
    useEffect(() => {
      setInputValue(value);
    }, [value]);
    
    const handleInputChange = (e) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      
      // Only update if it's a valid number
      if (newValue === '' || !isNaN(newValue)) {
        onChange(newValue === '' ? 0 : parseInt(newValue));
      }
    };

    const handleIncrement = () => {
      const newValue = (inputValue || 0) + 1;
      setInputValue(newValue);
      onChange(newValue);
    };

    const handleDecrement = () => {
      const newValue = Math.max(0, (inputValue || 0) - 1);
      setInputValue(newValue);
      onChange(newValue);
    };

    return (
      <div className="flex items-center">
        <button 
          onClick={handleDecrement}
          className="px-2 py-1 border rounded-l bg-gray-100 hover:bg-gray-200"
        >
          -
        </button>
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          className="w-16 text-center border-t border-b"
        />
        <button 
          onClick={handleIncrement}
          className="px-2 py-1 border rounded-r bg-gray-100 hover:bg-gray-200"
        >
          +
        </button>
        <span className="ml-2 text-sm text-gray-600">{unit}</span>
      </div>
    );
  };

  // State for secondary value color picker
  const [showSecondaryValueColorPicker, setShowSecondaryValueColorPicker] = useState(false);

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow p-4 flex justify-between items-center">
        <div className="flex items-center">
          <input
            type="text"
            value={dashboardName}
            onChange={(e) => setDashboardName(e.target.value)}
            className="text-xl font-bold bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500"
          />
          <div className="ml-4 text-sm text-gray-500">
            {elements.length} {elements.length === 1 ? 'element' : 'elements'}
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={initNewDashboard}
            className="flex items-center px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
          >
            <FaPlus className="mr-2" /> New Dashboard
          </button>
          <button
            onClick={openAddElementPanel}
            className="flex items-center px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 transition"
          >
            <FaLayerGroup className="mr-2" /> Add Element
          </button>
          <button
  onClick={saveDashboard}
  disabled={isSaving}
  className="flex items-center px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition disabled:opacity-50"
>
  <FaSave className="mr-2" /> 
  {isSaving ? 'Saving...' : 'Save Dashboard'}
</button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Add Elements */}
        {leftPanelOpen && (
          <div className="w-80 bg-white shadow-lg z-10 flex flex-col">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="font-bold text-lg">Add New Element</h2>
              <button 
                onClick={() => setLeftPanelOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-4">
                {/* Source Type Selection */}
                <div>
                  <label className="block text-sm font-medium mb-1">Select Data Source</label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="sourceType"
                        checked={sourceType === 'screen'}
                        onChange={() => setSourceType('screen')}
                        className="mr-2"
                      />
                      <span>Screen</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="sourceType"
                        checked={sourceType === 'view'}
                        onChange={() => setSourceType('view')}
                        className="mr-2"
                      />
                      <span>View</span>
                    </label>
                  </div>
                </div>
                
                {/* Screen Selection */}
                {sourceType === 'screen' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Select Screen</label>
                    <select
                      value={selectedScreen}
                      onChange={(e) => setSelectedScreen(e.target.value)}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">Select a screen</option>
                      {screensData.map(screen => (
                        <option key={screen.id} value={screen.name}>{screen.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* View Selection */}
                {sourceType === 'view' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Select View</label>
                    <select
                      value={selectedView}
                      onChange={(e) => setSelectedView(e.target.value)}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">Select a view</option>
                      {Object.keys(viewsData).map(viewName => (
                        <option key={viewName} value={viewName}>{viewName}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Table Selection (only for screens) */}
                {sourceType === 'screen' && selectedScreen && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Select Tables</label>
                    <div className="space-y-2">
                      {screensData.find(s => s.name === selectedScreen)?.headerTable && (
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedTables.header}
                            onChange={(e) => setSelectedTables({...selectedTables, header: e.target.checked})}
                            className="mr-2"
                          />
                          Header Table
                        </label>
                      )}
                      {screensData.find(s => s.name === selectedScreen)?.detailTable && (
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedTables.details}
                            onChange={(e) => setSelectedTables({...selectedTables, details: e.target.checked})}
                            className="mr-2"
                          />
                          Details Table
                        </label>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Loading indicator */}
                {isLoading && (
                  <div className="flex items-center justify-center p-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <span className="ml-2">Loading data...</span>
                  </div>
                )}
                
                {/* API Error */}
                {apiError && (
                  <div className="p-3 bg-red-50 text-red-700 rounded-md">
                    {apiError}
                  </div>
                )}
                
                {/* Data Preview */}
                {!isLoading && !apiError && 
                  ((sourceType === 'screen' && selectedScreen && (selectedTables.header || selectedTables.details)) ||
                   (sourceType === 'view' && selectedView)) && 
                  (apiData.header.length > 0 || apiData.details.length > 0) && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Data Preview</label>
                    <div className="text-xs text-gray-500 mb-2">
                      {sourceType === 'screen' ? (
                        <>
                          {selectedTables.header && `Header: ${apiData.header.length} records`}
                          {selectedTables.details && ` | Details: ${apiData.details.length} records`}
                        </>
                      ) : (
                        `Records: ${apiData.header.length}`
                      )}
                    </div>
                    <div className="max-h-32 overflow-y-auto border rounded p-2 bg-gray-50">
                      <table className="min-w-full text-xs">
                        <thead>
                          <tr>
                            {selectedColumns.slice(0, 3).map((col, idx) => (
                              <th key={idx} className="py-1 px-2 text-left">{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(apiData.header.slice(0, 2)).map((row, rowIdx) => (
                            <tr key={rowIdx}>
                              {selectedColumns.slice(0, 3).map((col, colIdx) => (
                                <td key={colIdx} className="py-1 px-2">{row[col]}</td>
                              ))}
                            </tr>
                          ))}
                          {sourceType === 'screen' && apiData.details.length > 0 && (
                            <>
                              <tr>
                                <td colSpan={selectedColumns.slice(0, 3).length} className="py-1 px-2 text-center bg-gray-200 font-semibold">
                                  Details Records
                                </td>
                              </tr>
                              {apiData.details.slice(0, 2).map((row, rowIdx) => (
                                <tr key={`detail-${rowIdx}`}>
                                  {selectedColumns.slice(0, 3).map((col, colIdx) => (
                                    <td key={colIdx} className="py-1 px-2">{row[col]}</td>
                                  ))}
                                </tr>
                              ))}
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* Column Selection */}
                {shouldShowColumnOptions() && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Select Columns</label>
                    <div className="max-h-40 overflow-y-auto border rounded p-2">
                      {availableColumns.map(column => (
                        <label key={column} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedColumns.includes(column)}
                            onChange={() => toggleColumnSelection(column)}
                            className="mr-2"
                          />
                          {column}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Column Filters */}
                {selectedColumns.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium mb-1 flex items-center">
                      <FaFilter className="mr-2" /> Column Filters
                    </label>
                    <div className="space-y-2 max-h-40 overflow-y-auto border rounded p-2">
                      {selectedColumns.map(column => (
                        <div key={column}>
                          <label className="text-xs text-gray-500">{column}</label>
                          <input
                            type="text"
                            value={filters[column] || ''}
                            onChange={(e) => handleFilterChange(column, e.target.value)}
                            placeholder={`Filter ${column}`}
                            className="w-full p-2 border rounded"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Date Range Selection */}
                {selectedColumns.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium mb-1 flex items-center">
                      <FaCalendarAlt className="mr-2" /> Date Range
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-gray-500">From</label>
                        <input
                          type="month"
                          value={dateRange.from}
                          onChange={(e) => setDateRange({...dateRange, from: e.target.value})}
                          className="w-full p-2 border rounded"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">To</label>
                        <input
                          type="month"
                          value={dateRange.to}
                          onChange={(e) => setDateRange({...dateRange, to: e.target.value})}
                          className="w-full p-2 border rounded"
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Visualization Type Selection */}
                {selectedColumns.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Visualization Type</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setVisualizationType('table')}
                        className={`flex flex-col items-center p-3 rounded border ${visualizationType === 'table' ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                      >
                        <FaTable className="text-xl mb-1" />
                        <span>Table</span>
                      </button>
                      <button
                        onClick={() => setVisualizationType('bar')}
                        className={`flex flex-col items-center p-3 rounded border ${visualizationType === 'bar' ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                      >
                        <FaChartBar className="text-xl mb-1" />
                        <span>Bar Chart</span>
                      </button>
                      <button
                        onClick={() => setVisualizationType('line')}
                        className={`flex flex-col items-center p-3 rounded border ${visualizationType === 'line' ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                      >
                        <FaChartLine className="text-xl mb-1" />
                        <span>Line Chart</span>
                      </button>
                      <button
                        onClick={() => setVisualizationType('pie')}
                        className={`flex flex-col items-center p-3 rounded border ${visualizationType === 'pie' ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                      >
                        <FaChartPie className="text-xl mb-1" />
                        <span>Pie Chart</span>
                      </button>
                      <button
                        onClick={() => setVisualizationType('scatter')}
                        className={`flex flex-col items-center p-3 rounded border ${visualizationType === 'scatter' ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                      >
                        <FaChartBar className="text-xl mb-1" />
                        <span>Scatter Plot</span>
                      </button>
                      <button
                        onClick={() => setVisualizationType('doubleline')}
                        className={`flex flex-col items-center p-3 rounded border ${visualizationType === 'doubleline' ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                      >
                        <FaChartLine className="text-xl mb-1" />
                        <span>Double Line</span>
                      </button>
                      <button
                        onClick={() => setVisualizationType('dashedline')}
                        className={`flex flex-col items-center p-3 rounded border ${visualizationType === 'dashedline' ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                      >
                        <FaChartLine className="text-xl mb-1" />
                        <span>Dashed Line</span>
                      </button>
                      <button
                        onClick={() => setVisualizationType('area')}
                        className={`flex flex-col items-center p-3 rounded border ${visualizationType === 'area' ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                      >
                        <FaChartLine className="text-xl mb-1" />
                        <span>Area Chart</span>
                      </button>
                      <button
                        onClick={() => setVisualizationType('stackedarea')}
                        className={`flex flex-col items-center p-3 rounded border ${visualizationType === 'stackedarea' ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                      >
                        <FaChartLine className="text-xl mb-1" />
                        <span>Stacked Area</span>
                      </button>
                      <button
                        onClick={() => setVisualizationType('cardinalarea')}
                        className={`flex flex-col items-center p-3 rounded border ${visualizationType === 'cardinalarea' ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                      >
                        <FaChartLine className="text-xl mb-1" />
                        <span>Cardinal Area</span>
                      </button>
                      <button
                        onClick={() => setVisualizationType('stackbar')}
                        className={`flex flex-col items-center p-3 rounded border ${visualizationType === 'stackbar' ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                      >
                        <FaChartBar className="text-xl mb-1" />
                        <span>Stack Bar</span>
                      </button>
                      <button
                        onClick={() => setVisualizationType('composed')}
                        className={`flex flex-col items-center p-3 rounded border ${visualizationType === 'composed' ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                      >
                        <FaChartBar className="text-xl mb-1" />
                        <span>Composed</span>
                      </button>
                      <button
                        onClick={() => setVisualizationType('samedatacomposed')}
                        className={`flex flex-col items-center p-3 rounded border ${visualizationType === 'samedatacomposed' ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                      >
                        <FaChartBar className="text-xl mb-1" />
                        <span>Same Data Composed</span>
                      </button>
                      <button
                        onClick={() => setVisualizationType('twolevelpie')}
                        className={`flex flex-col items-center p-3 rounded border ${visualizationType === 'twolevelpie' ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                      >
                        <FaChartPie className="text-xl mb-1" />
                        <span>Two Level Pie</span>
                      </button>
                      <button
                        onClick={() => setVisualizationType('straightanglepie')}
                        className={`flex flex-col items-center p-3 rounded border ${visualizationType === 'straightanglepie' ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                      >
                        <FaChartPie className="text-xl mb-1" />
                        <span>Straight Angle Pie</span>
                      </button>
                      <button
                        onClick={() => setVisualizationType('customactiveshape')}
                        className={`flex flex-col items-center p-3 rounded border ${visualizationType === 'customactiveshape' ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                      >
                        <FaChartPie className="text-xl mb-1" />
                        <span>Custom Active Shape</span>
                      </button>
                      <button
                        onClick={() => setVisualizationType('gaproundedcorners')}
                        className={`flex flex-col items-center p-3 rounded border ${visualizationType === 'gaproundedcorners' ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                      >
                        <FaChartPie className="text-xl mb-1" />
                        <span>Gap Rounded Corners</span>
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Chart Configuration */}
                {(visualizationType === 'bar' || visualizationType === 'line' || visualizationType === 'pie' || visualizationType === 'scatter' || 
                  visualizationType === 'doubleline' || visualizationType === 'dashedline' || visualizationType === 'area' || 
                  visualizationType === 'stackedarea' || visualizationType === 'cardinalarea' || visualizationType === 'stackbar' || 
                  visualizationType === 'composed' || visualizationType === 'samedatacomposed' || visualizationType === 'twolevelpie' || 
                  visualizationType === 'straightanglepie' || visualizationType === 'customactiveshape' || visualizationType === 'gaproundedcorners') && 
                  selectedColumns.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Chart Configuration</label>
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs text-gray-500">
                          {visualizationType === 'scatter' ? 'X-Axis' : 'Group By'}
                        </label>
                        <select
                          value={chartConfig.groupBy}
                          onChange={(e) => setChartConfig({...chartConfig, groupBy: e.target.value})}
                          className="w-full p-2 border rounded"
                        >
                          <option value="">Select column</option>
                          {selectedColumns.map(col => (
                            <option key={col} value={col}>{col}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">
                          {visualizationType === 'scatter' ? 'Y-Axis' : 'Value Field'}
                        </label>
                        <select
                          value={chartConfig.valueField}
                          onChange={(e) => setChartConfig({...chartConfig, valueField: e.target.value})}
                          className="w-full p-2 border rounded"
                        >
                          <option value="">Select column</option>
                          {selectedColumns.map(col => (
                            <option key={col} value={col}>{col}</option>
                          ))}
                        </select>
                      </div>
                      {/* UPDATED: Added second value field selector for double-valued charts */}
                      {(visualizationType === 'doubleline' || visualizationType === 'stackedarea' || visualizationType === 'stackbar' || visualizationType === 'composed') && (
                        <div>
                          <label className="text-xs text-gray-500">Secondary Value Field</label>
                          <select
                            value={chartConfig.valueField2 || ''}
                            onChange={(e) => setChartConfig({...chartConfig, valueField2: e.target.value})}
                            className="w-full p-2 border rounded"
                          >
                            <option value="">Select column</option>
                            {selectedColumns
                              .filter(col => col !== chartConfig.valueField)
                              .map(col => (
                                <option key={col} value={col}>{col}</option>
                              ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* User Assignment */}
                {visualizationType && (
                  <div>
                    <label className="block text-sm font-medium mb-1 flex items-center">
                      <FaUsers className="mr-2" /> Assign to Users
                    </label>
                    <div className="max-h-40 overflow-y-auto border rounded p-2">
                      {mockUsers.map(user => (
                        <label key={user.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user.id)}
                            onChange={() => toggleUserSelection(user.id)}
                            className="mr-2"
                          />
                          {user.name} ({user.role})
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                
                {visualizationType && (
                  <button
                    onClick={addElement}
                    className="w-full flex items-center justify-center py-2 bg-green-500 text-white rounded hover:bg-green-600 transition"
                  >
                    Add to Dashboard
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Main Canvas */}
        <div className="flex-1 bg-gray-200 p-4 overflow-auto">
          <div 
            ref={containerRef}
            className="bg-white shadow-lg rounded-lg overflow-auto relative"
            style={{ 
              minHeight: containerHeight,
              height: 'auto'
            }}
          >
            {elements.map((element) => (
              <Rnd
                key={element.id}
                default={{
                  x: element.x,
                  y: element.y,
                  width: element.width,
                  height: element.height
                }}
                position={{ x: element.x, y: element.y }}
                size={{ width: element.width, height: element.height }}
                onDragStop={(e, d) => {
                  updateElement(element.id, { x: d.x, y: d.y });
                }}
                onResizeStop={(e, direction, ref, delta, position) => {
                  updateElement(element.id, {
                    width: parseInt(ref.style.width),
                    height: parseInt(ref.style.height),
                    ...position,
                  });
                }}
                onClick={() => handleElementSelect(element.id)}
                bounds="parent"
                minWidth={100}
                minHeight={50}
                style={{ 
                  zIndex: selectedId === element.id ? 10 : 1,
                  border: selectedId === element.id ? '2px solid #3b82f6' : 'none'
                }}
              >
                {renderElement(element)}
              </Rnd>
            ))}
            
            {elements.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center p-8 bg-white bg-opacity-80 rounded-lg">
                  <h3 className="text-xl font-bold text-gray-700 mb-2">Your Dashboard is Empty</h3>
                  <p className="text-gray-500 mb-4">Click "Add Element" to get started</p>
                  <button
                    onClick={openAddElementPanel}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                  >
                    Add Your First Element
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar - Element Properties */}
        {rightPanelOpen && selectedElement && (
          <div className="w-80 bg-white shadow-lg z-10 flex flex-col">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="font-bold text-lg">Element Properties</h2>
              <button 
                onClick={() => setRightPanelOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Element Type</label>
                  <div className="p-2 bg-gray-100 rounded capitalize">
                    {selectedElement.type}
                    {selectedElement.type === 'chart' && ` (${selectedElement.chartType})`}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Data Source</label>
                  <div className="p-2 bg-gray-100 rounded">
                    {selectedElement.sourceType === 'screen' ? (
                      <div>
                        <div className="font-medium">Screen: {selectedElement.sourceName}</div>
                        {selectedElement.tables.header && <div className="text-xs">Header Table</div>}
                        {selectedElement.tables.details && <div className="text-xs">Details Table</div>}
                      </div>
                    ) : (
                      <div>
                        <div className="font-medium">View: {selectedElement.sourceName}</div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Title and Alignment */}
                <div>
                  <label className="block text-sm font-medium mb-1">Title</label>
                  <input
                    type="text"
                    value={selectedElement.title || ''}
                    onChange={(e) => updateElement(selectedId, { title: e.target.value })}
                    placeholder="Enter title (leave blank for no title)"
                    className="w-full p-2 border rounded"
                  />
                </div>
                
                {selectedElement.title && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1">Title Alignment</label>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => updateElement(selectedId, { titleAlignment: 'left' })}
                          className={`flex-1 p-2 rounded border ${selectedElement.titleAlignment === 'left' ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                        >
                          <FaAlignLeft className="mx-auto" />
                        </button>
                        <button
                          onClick={() => updateElement(selectedId, { titleAlignment: 'center' })}
                          className={`flex-1 p-2 rounded border ${selectedElement.titleAlignment === 'center' ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                        >
                          <FaAlignCenter className="mx-auto" />
                        </button>
                        <button
                          onClick={() => updateElement(selectedId, { titleAlignment: 'right' })}
                          className={`flex-1 p-2 rounded border ${selectedElement.titleAlignment === 'right' ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                        >
                          <FaAlignRight className="mx-auto" />
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Title Padding (px)</label>
                      <FigmaInput
                        value={selectedElement.titlePadding || 0}
                        onChange={(value) => updateElement(selectedId, { titlePadding: value })}
                        unit="px"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Title Font Size</label>
                      <FigmaInput
                        value={selectedElement.titleFontSize || 14}
                        onChange={(value) => updateElement(selectedId, { titleFontSize: value })}
                        unit="px"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Title Color</label>
                      <div className="flex items-center">
                        <div
                          className="w-8 h-8 border rounded cursor-pointer mr-2"
                          style={{ backgroundColor: selectedElement.titleColor || '#000000' }}
                          onClick={() => setShowTitleColorPicker(!showTitleColorPicker)}
                        />
                        <span className="text-sm">{selectedElement.titleColor || '#000000'}</span>
                      </div>
                      {showTitleColorPicker && (
                        <div className="mt-2">
                          <ChromePicker
                            color={selectedElement.titleColor || '#000000'}
                            onChangeComplete={(color) => {
                              updateElement(selectedId, { titleColor: color.hex });
                              setShowTitleColorPicker(false);
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}
                
                {/* Additional Columns Selection */}
                <div>
                  <label className="block text-sm font-medium mb-1">Additional Columns</label>
                  <div className="max-h-40 overflow-y-auto border rounded p-2">
                    {getAvailableColumnsForElement(selectedElement).map(column => (
                      <label key={column} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedElement.columns.includes(column)}
                          onChange={() => handleAdditionalColumnSelection(selectedElement.id, column)}
                          className="mr-2"
                        />
                        {column}
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Color Customization */}
                <div>
                  <label className="block text-sm font-medium mb-1 flex items-center">
                    <FaPalette className="mr-2" /> Color Customization
                  </label>
                  
                  {/* Background Color */}
                  <div className="mb-3">
                    <label className="text-xs text-gray-500">Background Color</label>
                    <div className="flex items-center">
                      <div
                        className="w-8 h-8 border rounded cursor-pointer mr-2"
                        style={{ backgroundColor: selectedElement.backgroundColor || '#ffffff' }}
                        onClick={() => setShowBackgroundColorPicker(!showBackgroundColorPicker)}
                      />
                      <span className="text-sm">{selectedElement.backgroundColor || '#ffffff'}</span>
                    </div>
                    {showBackgroundColorPicker && (
                      <div className="mt-2">
                        <ChromePicker
                          color={selectedElement.backgroundColor || '#ffffff'}
                          onChangeComplete={(color) => {
                            updateElement(selectedId, { backgroundColor: color.hex });
                            setShowBackgroundColorPicker(false);
                          }}
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* Chart/Line/Bar Color */}
                  {(selectedElement.type === 'bar' || selectedElement.type === 'line' || 
                    selectedElement.type === 'doubleline' || selectedElement.type === 'dashedline' || 
                    selectedElement.type === 'area' || selectedElement.type === 'stackedarea' || 
                    selectedElement.type === 'cardinalarea' || selectedElement.type === 'stackbar' || 
                    selectedElement.type === 'composed' || selectedElement.type === 'samedatacomposed') && (
                    <div className="mb-3">
                      <label className="text-xs text-gray-500">
                        {selectedElement.type === 'bar' || selectedElement.type === 'stackbar' ? 'Bar Color' : 
                         selectedElement.type === 'line' || selectedElement.type === 'doubleline' || selectedElement.type === 'dashedline' ? 'Line Color' : 
                         'Area Color'}
                      </label>
                      <div className="flex items-center">
                        <div
                          className="w-8 h-8 border rounded cursor-pointer mr-2"
                          style={{ backgroundColor: selectedElement.fill || '#000000' }}
                          onClick={() => setShowColorPicker(!showColorPicker)}
                        />
                        <span className="text-sm">{selectedElement.fill || '#000000'}</span>
                      </div>
                      {showColorPicker && (
                        <div className="mt-2">
                          <ChromePicker
                            color={selectedElement.fill || '#000000'}
                            onChangeComplete={(color) => {
                              updateElement(selectedId, { fill: color.hex });
                              setShowColorPicker(false);
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Secondary Value Color for Double-Valued Charts */}
                  {(selectedElement.type === 'doubleline' || 
                    selectedElement.type === 'stackedarea' || 
                    selectedElement.type === 'stackbar' || 
                    selectedElement.type === 'composed' || 
                    selectedElement.type === 'samedatacomposed') && (
                    <div className="mb-3">
                      <label className="text-xs text-gray-500">Secondary Value Color</label>
                      <div className="flex items-center">
                        <div
                          className="w-8 h-8 border rounded cursor-pointer mr-2"
                          style={{ backgroundColor: selectedElement.secondaryValueColor || COLORS[1] }}
                          onClick={() => setShowSecondaryValueColorPicker(!showSecondaryValueColorPicker)}
                        />
                        <span className="text-sm">{selectedElement.secondaryValueColor || COLORS[1]}</span>
                      </div>
                      {showSecondaryValueColorPicker && (
                        <div className="mt-2">
                          <ChromePicker
                            color={selectedElement.secondaryValueColor || COLORS[1]}
                            onChangeComplete={(color) => {
                              updateElement(selectedId, { secondaryValueColor: color.hex });
                              setShowSecondaryValueColorPicker(false);
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Table Colors */}
                  {selectedElement.type === 'table' && (
                    <>
                      <div className="mb-3">
                        <label className="text-xs text-gray-500">Table Header Color</label>
                        <div className="flex items-center">
                          <div
                            className="w-8 h-8 border rounded cursor-pointer mr-2"
                            style={{ backgroundColor: selectedElement.tableHeaderColor || '#f3f4f6' }}
                            onClick={() => setShowTableColorPicker(!showTableColorPicker)}
                          />
                          <span className="text-sm">{selectedElement.tableHeaderColor || '#f3f4f6'}</span>
                        </div>
                        {showTableColorPicker && (
                          <div className="mt-2">
                            <ChromePicker
                              color={selectedElement.tableHeaderColor || '#f3f4f6'}
                              onChangeComplete={(color) => {
                                updateElement(selectedId, { tableHeaderColor: color.hex });
                                setShowTableColorPicker(false);
                              }}
                            />
                          </div>
                        )}
                      </div>
                      
                      <div className="mb-3">
                        <label className="text-xs text-gray-500">Table Row Color</label>
                        <div className="flex items-center">
                          <div
                            className="w-8 h-8 border rounded cursor-pointer mr-2"
                            style={{ backgroundColor: selectedElement.tableRowColor || '#ffffff' }}
                            onClick={() => {
                              const newColor = selectedElement.tableRowColor === '#ffffff' ? '#f9fafb' : '#ffffff';
                              updateElement(selectedId, { tableRowColor: newColor });
                            }}
                          />
                          <span className="text-sm">{selectedElement.tableRowColor || '#ffffff'}</span>
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-xs text-gray-500">Table Alt Row Color</label>
                        <div className="flex items-center">
                          <div
                            className="w-8 h-8 border rounded cursor-pointer mr-2"
                            style={{ backgroundColor: selectedElement.tableAltRowColor || '#f9fafb' }}
                            onClick={() => {
                              const newColor = selectedElement.tableAltRowColor === '#f9fafb' ? '#ffffff' : '#f9fafb';
                              updateElement(selectedId, { tableAltRowColor: newColor });
                            }}
                          />
                          <span className="text-sm">{selectedElement.tableAltRowColor || '#f9fafb'}</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                
                {/* Axis Labels Customization */}
                {(selectedElement.type === 'bar' || selectedElement.type === 'line' || selectedElement.type === 'scatter' || 
                  selectedElement.type === 'doubleline' || selectedElement.type === 'dashedline' || selectedElement.type === 'area' || 
                  selectedElement.type === 'stackedarea' || selectedElement.type === 'cardinalarea' || selectedElement.type === 'stackbar' || 
                  selectedElement.type === 'composed' || selectedElement.type === 'samedatacomposed') && (
                  <div>
                    <label className="block text-sm font-medium mb-1 flex items-center">
                      <FaTextHeight className="mr-2" /> Axis Labels
                    </label>
                    
                    <div className="mb-3">
                      <label className="text-xs text-gray-500">Select Axis to Edit</label>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setEditingAxis(editingAxis === 'x' ? null : 'x')}
                          className={`flex-1 p-2 rounded border ${editingAxis === 'x' ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                        >
                          X-Axis
                        </button>
                        <button
                          onClick={() => setEditingAxis(editingAxis === 'y' ? null : 'y')}
                          className={`flex-1 p-2 rounded border ${editingAxis === 'y' ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
                        >
                          Y-Axis
                        </button>
                      </div>
                    </div>
                    
                    {editingAxis && (
                      <div className="border rounded p-3 bg-gray-50">
                        <h3 className="font-medium mb-2">{editingAxis.toUpperCase()}-Axis Label</h3>
                        
                        <div className="mb-3">
                          <label className="text-xs text-gray-500">Label Text</label>
                          <div className="flex items-center">
                            <input
                              type="text"
                              value={editingAxis === 'x' ? xLabelProps.text : yLabelProps.text}
                              onChange={(e) => updateAxisLabel(editingAxis, { text: e.target.value })}
                              className="flex-1 p-2 border rounded-l"
                              placeholder="Enter label text"
                            />
                            <button
                              onClick={() => {
                                if (editingAxis === 'x') {
                                  setEditingAxis('x');
                                } else {
                                  setEditingAxis('y');
                                }
                              }}
                              className="px-3 py-2 bg-blue-500 text-white rounded-r"
                            >
                              <FaEdit />
                            </button>
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <label className="text-xs text-gray-500">Position</label>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-xs text-gray-500">X</label>
                              <input
                                type="number"
                                value={editingAxis === 'x' ? xLabelProps.x : yLabelProps.x}
                                onChange={(e) => updateAxisLabel(editingAxis, { x: parseInt(e.target.value) })}
                                className="w-full p-2 border rounded"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500">Y</label>
                              <input
                                type="number"
                                value={editingAxis === 'x' ? xLabelProps.y : yLabelProps.y}
                                onChange={(e) => updateAxisLabel(editingAxis, { y: parseInt(e.target.value) })}
                                className="w-full p-2 border rounded"
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <label className="text-xs text-gray-500">Rotation (degrees)</label>
                          <input
                            type="range"
                            min="-180"
                            max="180"
                            value={editingAxis === 'x' ? xLabelProps.rotation : yLabelProps.rotation}
                            onChange={(e) => updateAxisLabel(editingAxis, { rotation: parseInt(e.target.value) })}
                            className="w-full"
                          />
                          <div className="text-center">{editingAxis === 'x' ? xLabelProps.rotation : yLabelProps.rotation}°</div>
                        </div>
                        
                        <div className="mb-3">
                          <label className="text-xs text-gray-500">Color</label>
                          <div className="flex items-center">
                            <div
                              className="w-8 h-8 border rounded cursor-pointer mr-2"
                              style={{ backgroundColor: editingAxis === 'x' ? xLabelProps.color : yLabelProps.color }}
                              onClick={() => {
                                if (editingAxis === 'x') {
                                  setShowXColorPicker(!showXColorPicker);
                                  setShowYColorPicker(false);
                                } else {
                                  setShowYColorPicker(!showYColorPicker);
                                  setShowXColorPicker(false);
                                }
                              }}
                            />
                            <span className="text-sm">{editingAxis === 'x' ? xLabelProps.color : yLabelProps.color}</span>
                          </div>
                          {editingAxis === 'x' && showXColorPicker && (
                            <div className="mt-2">
                              <ChromePicker
                                color={xLabelProps.color}
                                onChangeComplete={(color) => {
                                  updateAxisLabel('x', { color: color.hex });
                                  setShowXColorPicker(false);
                                }}
                              />
                            </div>
                          )}
                          {editingAxis === 'y' && showYColorPicker && (
                            <div className="mt-2">
                              <ChromePicker
                                color={yLabelProps.color}
                                onChangeComplete={(color) => {
                                  updateAxisLabel('y', { color: color.hex });
                                  setShowYColorPicker(false);
                                }}
                              />
                            </div>
                          )}
                        </div>
                        
                        <div className="mb-3">
                          <label className="text-xs text-gray-500">Font Size</label>
                          <FigmaInput
                            value={editingAxis === 'x' ? xLabelProps.fontSize : yLabelProps.fontSize}
                            onChange={(value) => updateAxisLabel(editingAxis, { fontSize: value })}
                            unit="px"
                          />
                        </div>
                        
                        <div>
                          <label className="text-xs text-gray-500">Font Family</label>
                          <select
                            value={editingAxis === 'x' ? xLabelProps.fontFamily : yLabelProps.fontFamily}
                            onChange={(e) => updateAxisLabel(editingAxis, { fontFamily: e.target.value })}
                            className="w-full p-2 border rounded"
                          >
                            <option value="Arial">Arial</option>
                            <option value="Verdana">Verdana</option>
                            <option value="Georgia">Georgia</option>
                            <option value="Times New Roman">Times New Roman</option>
                            <option value="Courier New">Courier New</option>
                          </select>
                        </div>
                      </div>
                    )}
                    
                    {!editingAxis && (
                      <div className="text-xs text-gray-500 italic">
                        Select an axis to customize its label properties
                      </div>
                    )}
                  </div>
                )}
                
                {selectedElement.type === 'text' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Text Content</label>
                      <textarea
                        value={selectedElement.text}
                        onChange={(e) => updateElement(selectedId, { text: e.target.value })}
                        className="w-full p-2 border rounded"
                        rows={3}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Font Size</label>
                      <input
                        type="range"
                        min="10"
                        max="72"
                        value={selectedElement.fontSize}
                        onChange={(e) => updateElement(selectedId, { fontSize: parseInt(e.target.value) })}
                        className="w-full"
                      />
                      <div className="text-center">{selectedElement.fontSize}px</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Font Family</label>
                      <select
                        value={selectedElement.fontFamily}
                        onChange={(e) => updateElement(selectedId, { fontFamily: e.target.value })}
                        className="w-full p-2 border rounded"
                      >
                        <option value="Arial">Arial</option>
                        <option value="Verdana">Verdana</option>
                        <option value="Georgia">Georgia</option>
                        <option value="Times New Roman">Times New Roman</option>
                        <option value="Courier New">Courier New</option>
                      </select>
                    </div>
                  </div>
                )}

                {selectedElement.type === 'table' && (
                  <div>
                    <div className="text-sm text-gray-600">
                      Records: {selectedElement.data ? selectedElement.data.length : 0}
                    </div>
                  </div>
                )}

                {(selectedElement.type === 'bar' || selectedElement.type === 'line' || selectedElement.type === 'pie' || selectedElement.type === 'scatter' || 
                  selectedElement.type === 'doubleline' || selectedElement.type === 'dashedline' || selectedElement.type === 'area' || 
                  selectedElement.type === 'stackedarea' || selectedElement.type === 'cardinalarea' || selectedElement.type === 'stackbar' || 
                  selectedElement.type === 'composed' || selectedElement.type === 'samedatacomposed' || selectedElement.type === 'twolevelpie' || 
                  selectedElement.type === 'straightanglepie' || selectedElement.type === 'customactiveshape' || selectedElement.type === 'gaproundedcorners') && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Chart Type</label>
                      <select
                        value={selectedElement.type}
                        onChange={(e) => updateElement(selectedId, { type: e.target.value })}
                        className="w-full p-2 border rounded"
                      >
                        <option value="bar">Bar Chart</option>
                        <option value="line">Line Chart</option>
                        <option value="doubleline">Double Line Chart</option>
                        <option value="dashedline">Dashed Line Chart</option>
                        <option value="area">Area Chart</option>
                        <option value="stackedarea">Stacked Area Chart</option>
                        <option value="cardinalarea">Cardinal Area Chart</option>
                        <option value="stackbar">Stack Bar Chart</option>
                        <option value="composed">Composed Chart</option>
                        <option value="samedatacomposed">Same Data Composed Chart</option>
                        <option value="pie">Pie Chart</option>
                        <option value="twolevelpie">Two Level Pie Chart</option>
                        <option value="straightanglepie">Straight Angle Pie Chart</option>
                        <option value="customactiveshape">Custom Active Shape Pie Chart</option>
                        <option value="gaproundedcorners">Pie Chart with Gap Rounded Corners</option>
                        <option value="scatter">Scatter Plot</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {selectedElement.type === 'scatter' ? 'X-Axis' : 'Group By'}
                      </label>
                      <select
                        value={selectedElement.chartConfig.groupBy}
                        onChange={(e) => updateElement(selectedId, { 
                          chartConfig: {...selectedElement.chartConfig, groupBy: e.target.value}
                        })}
                        className="w-full p-2 border rounded"
                      >
                        <option value="">Select column</option>
                        {selectedElement.columns.map(col => (
                          <option key={col} value={col}>{col}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {selectedElement.type === 'scatter' ? 'Y-Axis' : 'Value Field'}
                      </label>
                      <select
                        value={selectedElement.chartConfig.valueField}
                        onChange={(e) => updateElement(selectedId, { 
                          chartConfig: {...selectedElement.chartConfig, valueField: e.target.value}
                        })}
                        className="w-full p-2 border rounded"
                      >
                        <option value="">Select column</option>
                        {selectedElement.columns.map(col => (
                          <option key={col} value={col}>{col}</option>
                        ))}
                      </select>
                    </div>
                    {/* UPDATED: Added second value field selector in properties panel */}
                    {(selectedElement.type === 'doubleline' || selectedElement.type === 'stackedarea' || selectedElement.type === 'stackbar' || selectedElement.type === 'composed') && (
                      <div>
                        <label className="block text-sm font-medium mb-1">Secondary Value Field</label>
                        <select
                          value={selectedElement.chartConfig.valueField2 || ''}
                          onChange={(e) => updateElement(selectedId, { 
                            chartConfig: {...selectedElement.chartConfig, valueField2: e.target.value}
                          })}
                          className="w-full p-2 border rounded"
                        >
                          <option value="">Select column</option>
                          {selectedElement.columns
                            .filter(col => col !== selectedElement.chartConfig.valueField)
                            .map(col => (
                              <option key={col} value={col}>{col}</option>
                            ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-1">Size</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-500">Width</label>
                      <input
                        type="number"
                        value={selectedElement.width}
                        onChange={(e) => updateElement(selectedId, { width: parseInt(e.target.value) })}
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Height</label>
                      <input
                        type="number"
                        value={selectedElement.height}
                        onChange={(e) => updateElement(selectedId, { height: parseInt(e.target.value) })}
                        className="w-full p-2 border rounded"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Animation</label>
                  <select
                    value={selectedElement.animation}
                    onChange={(e) => applyAnimation(e.target.value)}
                    className="w-full p-2 border rounded"
                  >
                    <option value="none">None</option>
                    <option value="fadeUp">Fade Up</option>
                    <option value="fadeDown">Fade Down</option>
                    <option value="fadeIn">Fade In</option>
                    <option value="slideIn">Slide In</option>
                  </select>
                </div>

                <button
                  onClick={deleteElement}
                  className="w-full flex items-center justify-center py-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
                >
                  <FaTrash className="mr-2" /> Delete Element
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {showAssignModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
      <h2 className="text-xl font-bold mb-4">Assign Users to Dashboard</h2>
      <div className="mb-4">
        <h3 className="font-medium">{dashboardName}</h3>
      </div>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Select Users</label>
        <div className="max-h-60 overflow-y-auto border rounded p-2">
          {users.length === 0 ? (
            <div className="text-center py-4 text-gray-500">No users available</div>
          ) : (
            users.map(user => (
              <label key={user.UserID} className="flex items-center p-2 hover:bg-gray-50 rounded">
                <input
                  type="checkbox"
                  checked={selectedUsers.includes(user.UserID)}
                  onChange={() => toggleUserSelection(user.UserID)}
                  className="mr-2"
                />
                <div>
                  <div className="font-medium">{user.FullName}</div>
                  <div className="text-xs text-gray-500">{user.Email} - {user.Role}</div>
                </div>
              </label>
            ))
          )}
        </div>
      </div>
      
      <div className="flex justify-end space-x-2">
        <button
          onClick={() => setShowAssignModal(false)}
          className="px-4 py-2 border border-gray-300 rounded-lg"
        >
          Skip for Now
        </button>
        <button
          onClick={handleSaveAssignments}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Save Assignments
        </button>
      </div>
    </div>
  </div>
)}
      
    </div>
    
  );
};

export default DashboardBuilder;